import { Block, createBlock } from './types'

export interface ProposalTemplate {
  id: string
  name: string
  nameHe: string
  description: string
  descriptionHe: string
  category: 'basic' | 'professional' | 'creative' | 'industry'
  color: string
  icon: string
  coverImage: string
  blocks: Block[]
}

// Helper to create blocks with sequential order
function createTemplateBlocks(blockConfigs: Array<{ type: any; data?: any }>): Block[] {
  return blockConfigs.map((config, index) => {
    const block = createBlock(config.type, index)
    return { ...block, ...config.data }
  })
}

export const proposalTemplates: ProposalTemplate[] = [
  {
    id: 'basic-quote',
    name: 'Basic Quote',
    nameHe: 'Basic Quote',
    description: 'Simple quote with pricing table and signature',
    descriptionHe: 'Heading + Description + Pricing + Signature',
    category: 'basic',
    color: 'blue',
    icon: 'FileText',
    coverImage: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Price Proposal',
          subtitle: 'A custom proposal tailored for you',
          backgroundImage: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=600&fit=crop',
          backgroundColor: '#1e40af',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'text',
        data: {
          content: 'Dear Client,\n\nThank you for reaching out to us. We are pleased to present our detailed price proposal for the requested services.\n\nThis proposal includes all the necessary details and we are available for any questions or clarifications.',
          alignment: 'left'
        }
      },
      {
        type: 'divider',
        data: { style: 'solid' }
      },
      {
        type: 'heading',
        data: { content: 'Service Details', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Below is a full breakdown of the services and products included in this proposal:',
          alignment: 'left'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'item-1', description: 'Primary Service', quantity: 1, unitPrice: 1000, total: 1000 },
            { id: 'item-2', description: 'Additional Service', quantity: 1, unitPrice: 500, total: 500 },
            { id: 'item-3', description: 'Support & Guidance', quantity: 1, unitPrice: 300, total: 300 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'spacer',
        data: { height: 'medium' }
      },
      {
        type: 'heading',
        data: { content: 'Payment Terms', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Proposal validity: 30 days from date of issue',
            'Payment terms: Net 30',
            'Installment option: Up to 3 interest-free payments'
          ],
          style: 'check'
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Service Provider Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'full-project',
    name: 'Full Project Proposal',
    nameHe: 'Full Project Proposal',
    description: 'Comprehensive proposal with cover, details, and milestones',
    descriptionHe: 'Cover + Description + Phases + Pricing + Signature',
    category: 'professional',
    color: 'purple',
    icon: 'Briefcase',
    coverImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Project Proposal',
          subtitle: 'A professional and comprehensive solution for your needs',
          backgroundImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
          backgroundColor: '#e14d9a',
          overlay: true,
          overlayOpacity: 0.65
        }
      },
      {
        type: 'heading',
        data: { content: 'About the Project', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'We are pleased to present a comprehensive proposal for this project. The proposal is based on a deep understanding of your needs and years of experience in the field.\n\nThe project will be executed according to high professional standards while maintaining timelines and budget.',
          alignment: 'left'
        }
      },
      {
        type: 'divider',
        data: { style: 'solid' }
      },
      {
        type: 'heading',
        data: { content: 'Work Phases', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Phase 1: Planning & Specification - Requirements definition, research and needs analysis',
            'Phase 2: Initial Design - Concept creation and direction approval',
            'Phase 3: Development & Implementation - Executing the actual work',
            'Phase 4: Testing & Revisions - Quality control and adjustments',
            'Phase 5: Launch & Delivery - Handover to client and training'
          ],
          style: 'numbered'
        }
      },
      {
        type: 'heading',
        data: { content: 'Timeline', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Estimated project duration: 4-6 weeks\nEstimated start date: Immediately upon proposal approval and advance payment\n\nKey milestones:\n• Specification phase completion - Week 1\n• Design approval - Week 2\n• Development completion - Week 4\n• Final delivery - Week 5-6',
          alignment: 'left'
        }
      },
      {
        type: 'divider',
        data: { style: 'dashed' }
      },
      {
        type: 'heading',
        data: { content: 'Price Proposal', level: 2 }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'phase-1', description: 'Phase 1: Planning & Specification', quantity: 1, unitPrice: 2500, total: 2500 },
            { id: 'phase-2', description: 'Phase 2: Design', quantity: 1, unitPrice: 3500, total: 3500 },
            { id: 'phase-3', description: 'Phase 3: Development & Implementation', quantity: 1, unitPrice: 6000, total: 6000 },
            { id: 'phase-4', description: 'Phases 4-5: Testing, Launch & Training', quantity: 1, unitPrice: 2000, total: 2000 }
          ],
          showQuantity: false,
          showUnitPrice: false
        }
      },
      {
        type: 'quote',
        data: {
          content: 'The price includes one year of technical support and ongoing maintenance at no additional charge.',
          author: ''
        }
      },
      {
        type: 'heading',
        data: { content: 'Terms & Commitments', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Proposal validity: 14 days from date of issue',
            'Payment: 50% advance upon approval, 50% upon project completion',
            'Copyright transfers to client upon full payment',
            'Warranty: One year from delivery date'
          ],
          style: 'check'
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Service Provider Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'service-contract',
    name: 'Service Contract',
    nameHe: 'Service Agreement',
    description: 'Service agreement with terms and dual signatures',
    descriptionHe: 'Service terms + Pricing + Dual signature',
    category: 'professional',
    color: 'emerald',
    icon: 'Shield',
    coverImage: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Service Agreement',
          subtitle: 'A professional agreement for long-term engagement',
          backgroundImage: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
          backgroundColor: '#059669',
          overlay: true,
          overlayOpacity: 0.7
        }
      },
      {
        type: 'text',
        data: {
          content: 'This agreement is made and signed on: ________________\n\nBetween: __________________________ (hereinafter: "Service Provider")\nBusiness ID: ______________\nAddress: ________________________\n\nAnd: __________________________ (hereinafter: "Client")\nBusiness ID: ______________\nAddress: ________________________',
          alignment: 'left'
        }
      },
      {
        type: 'divider',
        data: { style: 'solid' }
      },
      {
        type: 'heading',
        data: { content: 'Whereas', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'The Service Provider is engaged in providing professional services in the field of _______',
            'The Client wishes to receive services from the Service Provider',
            'The parties wish to formalize the terms of their engagement'
          ],
          style: 'bullet'
        }
      },
      {
        type: 'heading',
        data: { content: 'Agreement Scope & Services', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'The Service Provider commits to providing the Client with the following services:',
          alignment: 'left'
        }
      },
      {
        type: 'list',
        data: {
          items: [
            'First Service - Detailed description of the service',
            'Second Service - Detailed description of the service',
            'Third Service - Detailed description of the service'
          ],
          style: 'numbered'
        }
      },
      {
        type: 'heading',
        data: { content: 'Payment Terms', level: 2 }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'service-1', description: 'One-time setup fee', quantity: 1, unitPrice: 1500, total: 1500 },
            { id: 'service-2', description: 'Monthly service fee', quantity: 12, unitPrice: 350, total: 4200 },
            { id: 'service-3', description: 'Extended technical support (optional)', quantity: 12, unitPrice: 150, total: 1800 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'heading',
        data: { content: 'General Terms', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Agreement period: 12 months from signing date',
            'Renewal: Automatic renewal for additional 12-month periods',
            'Cancellation: 30 days written notice required',
            'Confidentiality: Both parties commit to full confidentiality',
            'Jurisdiction: The competent courts in the relevant jurisdiction'
          ],
          style: 'numbered'
        }
      },
      {
        type: 'spacer',
        data: { height: 'large' }
      },
      {
        type: 'text',
        data: {
          content: 'In witness whereof, the parties have signed:',
          alignment: 'center'
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Service Provider Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'creative-portfolio',
    name: 'Creative Portfolio',
    nameHe: 'Creative Proposal',
    description: 'Visual proposal with gallery and creative layout',
    descriptionHe: 'Cover + Gallery + Description + Pricing',
    category: 'creative',
    color: 'pink',
    icon: 'Palette',
    coverImage: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Design Project',
          subtitle: 'A custom creative proposal',
          backgroundImage: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?w=1200&h=600&fit=crop',
          backgroundColor: '#ec4899',
          overlay: true,
          overlayOpacity: 0.5
        }
      },
      {
        type: 'quote',
        data: {
          content: 'Good design is not just about how it looks, but how it works.',
          author: 'Steve Jobs'
        }
      },
      {
        type: 'heading',
        data: { content: 'The Concept', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'We offer you a unique design experience that combines modern aesthetics with perfect functionality. Our concept is based on a deep understanding of your brand, target audience, and the messages you want to convey.\n\nEvery project receives personal attention and care for the smallest details.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Selected Works', level: 2 }
      },
      {
        type: 'gallery',
        data: {
          images: [
            'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1558655146-d09347e92766?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1572044162444-ad60f128bdea?w=400&h=300&fit=crop'
          ],
          columns: 3
        }
      },
      {
        type: 'divider',
        data: { style: 'dashed' }
      },
      {
        type: 'heading',
        data: { content: 'What the Package Includes', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Visual research & specification - Understanding the brand and audience',
            'Concept & initial design - 2-3 directions to choose from',
            '3 revision rounds - Until full satisfaction',
            'Source files - All files in professional formats',
            'Digital & print adaptation - Versions tailored for every use'
          ],
          style: 'check'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'design-1', description: 'Full design package', quantity: 1, unitPrice: 4500, total: 4500 },
            { id: 'design-2', description: 'Additional designs (as needed)', quantity: 2, unitPrice: 500, total: 1000 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Designer Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'consulting-proposal',
    name: 'Consulting Proposal',
    nameHe: 'Consulting Proposal',
    description: 'Professional consulting engagement proposal',
    descriptionHe: 'Consulting project proposal with methodology',
    category: 'professional',
    color: 'orange',
    icon: 'Briefcase',
    coverImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Strategic Consulting Proposal',
          subtitle: 'Tailored solutions for your business growth',
          backgroundImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=600&fit=crop',
          backgroundColor: '#f59e0b',
          overlay: true,
          overlayOpacity: 0.65
        }
      },
      {
        type: 'heading',
        data: { content: 'Why Us', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'We bring over 10 years of experience in business and strategic consulting, with hundreds of successful projects across diverse industries.\n\nOur methodology is based on a proven approach that combines data analysis, deep business understanding, and close guidance throughout the process.',
          alignment: 'left'
        }
      },
      {
        type: 'quote',
        data: {
          content: 'True success is measured by results - and we are committed to your results.',
          author: ''
        }
      },
      {
        type: 'heading',
        data: { content: 'Our Methodology', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Diagnosis & current state analysis - In-depth mapping of the current situation',
            'Goal setting & success metrics - Defining clear KPIs',
            'Strategic plan development - Detailed roadmap',
            'Implementation guidance - Close collaboration with your team',
            'Monitoring & optimization - Continuous improvement based on data'
          ],
          style: 'numbered'
        }
      },
      {
        type: 'divider',
        data: { style: 'solid' }
      },
      {
        type: 'heading',
        data: { content: 'Project Scope & Pricing', level: 2 }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'consult-1', description: 'Initial diagnosis & situation analysis', quantity: 1, unitPrice: 3500, total: 3500 },
            { id: 'consult-2', description: 'Strategy development & action plan', quantity: 1, unitPrice: 8500, total: 8500 },
            { id: 'consult-3', description: 'Monthly implementation guidance', quantity: 3, unitPrice: 2500, total: 7500 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'heading',
        data: { content: 'What\'s Included', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Weekly personal meetings',
            'Monthly progress reports',
            'Access to analysis and tracking tools',
            'Phone and email availability',
            'Summary and recommendations at project completion'
          ],
          style: 'check'
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Consultant Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'web-development',
    name: 'Web Development',
    nameHe: 'Website Development',
    description: 'Website development project proposal',
    descriptionHe: 'Website development proposal',
    category: 'creative',
    color: 'blue',
    icon: 'Palette',
    coverImage: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Website Development Proposal',
          subtitle: 'Custom design and development for your business',
          backgroundImage: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=1200&h=600&fit=crop',
          backgroundColor: '#3b82f6',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'heading',
        data: { content: 'Project Overview', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'We are pleased to present a comprehensive proposal for designing and developing a modern, responsive website.\n\nThe website will be designed and developed to fully match your unique needs, using the most advanced technologies and meeting the highest standards.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'What the Project Includes', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Custom UI/UX design - Perfect user experience',
            'Responsive development - Perfect display on any device',
            'Content management system - Easy and independent updates',
            'Search engine optimization (SEO) - Maximum visibility',
            'Advanced security - SSL and data protection',
            'Fast loading speed - Excellent performance'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Portfolio Examples', level: 2 }
      },
      {
        type: 'gallery',
        data: {
          images: [
            'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=400&h=300&fit=crop'
          ],
          columns: 3
        }
      },
      {
        type: 'divider',
        data: { style: 'solid' }
      },
      {
        type: 'heading',
        data: { content: 'Price Proposal', level: 2 }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'web-1', description: 'User interface design (UI/UX)', quantity: 1, unitPrice: 5500, total: 5500 },
            { id: 'web-2', description: 'Website development', quantity: 1, unitPrice: 8500, total: 8500 },
            { id: 'web-3', description: 'Content management system setup', quantity: 1, unitPrice: 2000, total: 2000 },
            { id: 'web-4', description: 'Annual hosting & maintenance', quantity: 1, unitPrice: 1200, total: 1200 }
          ],
          showQuantity: false,
          showUnitPrice: false
        }
      },
      {
        type: 'heading',
        data: { content: 'Timeline', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: '• Design phase: 1-2 weeks\n• Development phase: 2-3 weeks\n• Testing & launch: One week\n\nTotal: 4-6 weeks from proposal approval date',
          alignment: 'left'
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Developer Signature',
          showDate: true
        }
      }
    ])
  },
  // ===== INDUSTRY TEMPLATES =====

  {
    id: 'renovation-construction',
    name: 'Renovation & Construction',
    nameHe: 'Renovation & Construction',
    description: 'Proposal for contractors and construction projects',
    descriptionHe: 'Price proposal for renovation and construction work',
    category: 'industry',
    color: 'amber',
    icon: 'Hammer',
    coverImage: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Renovation Quote',
          subtitle: 'Professional renovation and construction work',
          backgroundImage: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&h=600&fit=crop',
          backgroundColor: '#d97706',
          overlay: true,
          overlayOpacity: 0.65
        }
      },
      {
        type: 'heading',
        data: { content: 'Work Scope Description', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Below is a detailed breakdown of the proposed work for this project. The proposal is based on a site visit and includes all required work, raw materials, labor, and professional supervision.\n\nAll work will be performed according to local building standards with licensed engineer oversight.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Materials Breakdown', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Porcelain floor tiles 60x60 - Italian, color per client selection',
            'Wall tiles for wet rooms - High-quality ceramic',
            'Water plumbing - Certified PEX pipes',
            'Electrical - Certified cables and standard electrical panel',
            'Drywall & ceilings - Moisture-resistant drywall in wet areas',
            'Paint - Primer + 2 finish coats, premium brand'
          ],
          style: 'check'
        }
      },
      {
        type: 'timeline',
        data: {
          title: 'Timeline & Milestones',
          items: [
            { id: 'reno-t1', title: 'Demolition & Dismantling', description: 'Wall demolition, floor and old plumbing removal', date: 'Week 1-2' },
            { id: 'reno-t2', title: 'Infrastructure', description: 'Plumbing, electrical, sewage and water pipes', date: 'Week 3-4' },
            { id: 'reno-t3', title: 'Plastering & Tiling', description: 'Plaster work, floor and wall tiling', date: 'Week 5-6' },
            { id: 'reno-t4', title: 'Finishing', description: 'Paint, hardware, carpentry, final electrical', date: 'Week 7-8' },
            { id: 'reno-t5', title: 'Cleanup & Handover', description: 'Final cleaning, last fixes and project handover', date: 'Week 9' }
          ],
          layout: 'vertical',
          color: '#d97706'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'reno-p1', description: 'Demolition and waste removal', quantity: 1, unitPrice: 8000, total: 8000 },
            { id: 'reno-p2', description: 'Plumbing and piping', quantity: 1, unitPrice: 15000, total: 15000 },
            { id: 'reno-p3', description: 'Electrical work', quantity: 1, unitPrice: 12000, total: 12000 },
            { id: 'reno-p4', description: 'Tiling and cladding (incl. materials)', quantity: 1, unitPrice: 25000, total: 25000 },
            { id: 'reno-p5', description: 'Plaster, spackle and paint', quantity: 1, unitPrice: 10000, total: 10000 },
            { id: 'reno-p6', description: 'Carpentry and hardware', quantity: 1, unitPrice: 18000, total: 18000 },
            { id: 'reno-p7', description: 'Engineering supervision', quantity: 1, unitPrice: 5000, total: 5000 }
          ],
          showQuantity: false,
          showUnitPrice: false
        }
      },
      {
        type: 'heading',
        data: { content: 'Payment Terms', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            '30% advance upon agreement signing',
            '30% upon completion of infrastructure phase',
            '30% upon completion of finishing work',
            '10% upon final delivery and client inspection'
          ],
          style: 'numbered'
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Warranty & Terms',
          items: [
            'Renovation work warranty: 3 years from delivery date',
            'Plumbing warranty: 5 years',
            'Waterproofing warranty: 10 years subject to regular maintenance',
            'Contractor insurance: Valid policy for the entire work period',
            'Delays: Contractor will notify in advance of any expected delays',
            'Changes: Any deviation from the original plan will be priced separately and approved in writing'
          ],
          style: 'numbered',
          theme: 'warranty',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Contractor Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'graphic-design',
    name: 'Graphic Design',
    nameHe: 'Graphic Design',
    description: 'Proposal for graphic design services',
    descriptionHe: 'Price proposal for graphic design services',
    category: 'creative',
    color: 'fuchsia',
    icon: 'Palette',
    coverImage: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Graphic Design Proposal',
          subtitle: 'Professional visual brand design',
          backgroundImage: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=1200&h=600&fit=crop',
          backgroundColor: '#c026d3',
          overlay: true,
          overlayOpacity: 0.55
        }
      },
      {
        type: 'heading',
        data: { content: 'Portfolio Examples', level: 2 }
      },
      {
        type: 'gallery',
        data: {
          images: [
            'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1558655146-d09347e92766?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1572044162444-ad60f128bdea?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=400&h=300&fit=crop'
          ],
          columns: 2
        }
      },
      {
        type: 'heading',
        data: { content: 'Our Work Process', level: 2 }
      },
      {
        type: 'timeline',
        data: {
          title: 'Design Phases',
          items: [
            { id: 'gd-t1', title: 'Brief & Research', description: 'Understanding the brand, target audience and messaging. Competitor and design trend analysis', date: 'Phase 1' },
            { id: 'gd-t2', title: 'Initial Concept', description: 'Presenting 2-3 design directions to choose from', date: 'Phase 2' },
            { id: 'gd-t3', title: 'Detailed Design', description: 'Developing the chosen direction with all details', date: 'Phase 3' },
            { id: 'gd-t4', title: 'Revisions & Refinement', description: 'Up to 3 revision rounds until full satisfaction', date: 'Phase 4' },
            { id: 'gd-t5', title: 'File Delivery', description: 'Delivering all files in professional formats', date: 'Phase 5' }
          ],
          layout: 'vertical',
          color: '#c026d3'
        }
      },
      {
        type: 'heading',
        data: { content: 'Deliverables', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Main logo + variations (color, black & white, icon)',
            'Brand guide (Brand Book) - Colors, typography, proper usage',
            'Business card - Double-sided design',
            'Branded letterhead and envelope',
            'Digital email signature',
            'Social media templates (Facebook, Instagram)',
            'Source files in formats: AI, PDF, PNG, SVG'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Revision Policy', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'The proposal includes up to 3 revision rounds per design phase. A revision round is defined as one consolidated feedback sent in writing. Revisions beyond the quota will be charged at $75/hour.\n\nA major design direction change after concept approval will be considered a separate project.',
          alignment: 'left'
        }
      },
      {
        type: 'packages',
        data: {
          title: 'Design Packages',
          packages: [
            {
              id: 'gd-basic',
              name: 'Basic',
              price: 3500,
              currency: '$',
              description: 'Basic logo and brand design',
              features: [
                'Logo design + 2 variations',
                'Business card',
                'Email signature',
                '2 revision rounds',
                'Files in digital formats'
              ]
            },
            {
              id: 'gd-pro',
              name: 'Professional',
              price: 6500,
              currency: '$',
              description: 'Comprehensive branding package',
              features: [
                'Logo design + all variations',
                'Full brand guide',
                'Business card + letterhead',
                'Email signature',
                'Social media templates',
                '3 revision rounds',
                'Full source files'
              ],
              highlighted: true
            },
            {
              id: 'gd-premium',
              name: 'Premium',
              price: 12000,
              currency: '$',
              description: 'Full branding + strategy',
              features: [
                'Everything in Professional package',
                'Brand strategy',
                'Packaging / print materials design',
                'Website design (mockup)',
                'Branded company presentation',
                'Unlimited revision rounds',
                'Brand guidance for 3 months'
              ]
            }
          ]
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Terms & Commitments',
          items: [
            'Copyright transfers to client upon full payment',
            'Proposal validity: 21 days from date of issue',
            'Payment: 50% advance, 50% upon final file delivery',
            'Estimated timeline: 2-4 weeks depending on selected package',
            'The designer reserves the right to showcase the work in their portfolio'
          ],
          style: 'numbered',
          theme: 'legal',
          variant: 'outlined',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Designer Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'photography',
    name: 'Photography',
    nameHe: 'Photography',
    description: 'Proposal for photography services',
    descriptionHe: 'Price proposal for photography services',
    category: 'creative',
    color: 'rose',
    icon: 'Camera',
    coverImage: 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Photography Services Proposal',
          subtitle: 'Moments that last forever',
          backgroundImage: 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=1200&h=600&fit=crop',
          backgroundColor: '#e11d48',
          overlay: true,
          overlayOpacity: 0.5
        }
      },
      {
        type: 'heading',
        data: { content: 'From Our Gallery', level: 2 }
      },
      {
        type: 'gallery',
        data: {
          images: [
            'https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1537633552985-df8429e8048b?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1605810230434-7631ac76ec81?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&h=300&fit=crop'
          ],
          columns: 3
        }
      },
      {
        type: 'packages',
        data: {
          title: 'Photography Packages',
          packages: [
            {
              id: 'photo-basic',
              name: 'Basic',
              price: 2500,
              currency: '$',
              description: 'Perfect for small events',
              features: [
                'Up to 4 hours of shooting',
                '150 edited photos',
                'Digital gallery for download',
                'Basic color and light editing',
                'Delivery within 14 business days'
              ]
            },
            {
              id: 'photo-premium',
              name: 'Premium',
              price: 4500,
              currency: '$',
              description: 'The most popular option',
              features: [
                'Up to 8 hours of shooting',
                '350 edited photos',
                'Digital gallery + digital album',
                'Full professional editing',
                'Preparation + event photos',
                'Delivery within 10 business days'
              ],
              highlighted: true
            },
            {
              id: 'photo-gold',
              name: 'Gold',
              price: 7500,
              currency: '$',
              description: 'The complete luxury experience',
              features: [
                'Full day unlimited shooting',
                'All photos edited',
                'Premium printed album 30x30',
                'Digital gallery + digital album',
                'Video highlights (3 minutes)',
                'Second photographer for the event',
                'Delivery within 7 business days'
              ]
            }
          ]
        }
      },
      {
        type: 'heading',
        data: { content: 'What Every Package Includes', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'Every package includes',
          items: [
            { icon: 'Camera', text: 'Advanced professional equipment - Premium cameras and lenses' },
            { icon: 'Palette', text: 'Professional editing - Color, light and retouching corrections' },
            { icon: 'Cloud', text: 'Secure online gallery for photo download' },
            { icon: 'Shield', text: 'Double backup of all photos' }
          ],
          columns: 2,
          iconColor: '#e11d48'
        }
      },
      {
        type: 'heading',
        data: { content: 'Usage Rights', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'The client receives full personal usage rights for the photos, including printing, sharing on social media, and personal use.\n\nCommercial use (advertising, marketing, sales) requires a separate agreement.\n\nThe photographer retains credit rights and the right to display the work in their portfolio.',
          alignment: 'left'
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Booking Terms',
          items: [
            'Date booking: A $150 advance is required to reserve the date',
            'Cancellation up to 30 days before: Full advance refund',
            'Cancellation 14-30 days before: 50% advance refund',
            'Cancellation less than 14 days: Advance is non-refundable',
            'Date rescheduling: Can be rescheduled once at no charge with 14 days notice',
            'Proposal validity: 14 days from date of issue'
          ],
          style: 'numbered',
          theme: 'cancellation',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Photographer Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'digital-marketing',
    name: 'Digital Marketing',
    nameHe: 'Digital Marketing',
    description: 'Proposal for digital marketing services',
    descriptionHe: 'Price proposal for digital marketing services',
    category: 'industry',
    color: 'cyan',
    icon: 'TrendingUp',
    coverImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Digital Marketing Proposal',
          subtitle: 'Digital strategy that drives results',
          backgroundImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
          backgroundColor: '#0891b2',
          overlay: true,
          overlayOpacity: 0.65
        }
      },
      {
        type: 'heading',
        data: { content: 'Strategic Overview', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Based on a preliminary analysis of your digital presence, we have identified significant growth opportunities. The proposed strategy combines multiple digital channels to create an effective marketing system that generates quality leads and increases revenue.',
          alignment: 'left'
        }
      },
      {
        type: 'stats',
        data: {
          title: '',
          items: [
            { id: 'dm-s1', value: '300%', label: 'Average ROI increase' },
            { id: 'dm-s2', value: '150+', label: 'Active clients' },
            { id: 'dm-s3', value: '10M+', label: 'Managed budget' },
            { id: 'dm-s4', value: '5+', label: 'Years of experience' }
          ],
          columns: 4,
          color: '#0891b2'
        }
      },
      {
        type: 'heading',
        data: { content: 'Service Details', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'Our Services',
          items: [
            { icon: 'Search', text: 'Google Ads - Search and display campaign management' },
            { icon: 'Users', text: 'Social media marketing - Facebook, Instagram, LinkedIn & TikTok' },
            { icon: 'Mail', text: 'Email marketing - List building, newsletters and automations' },
            { icon: 'FileText', text: 'Content & SEO - Content writing, optimization and organic growth' },
            { icon: 'BarChart', text: 'Analytics & reports - Tracking, analysis and ongoing optimization' },
            { icon: 'Target', text: 'Digital strategy - Comprehensive marketing roadmap planning' }
          ],
          columns: 2,
          iconColor: '#0891b2'
        }
      },
      {
        type: 'heading',
        data: { content: 'Success Metrics (KPIs)', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Cost per lead (CPL) - 30% reduction within 3 months',
            'Conversion rate - Increase to 3.5% and above',
            'Organic traffic - 50% growth within 6 months',
            'Social media reach & engagement - 200% growth',
            'Paid advertising ROI - Target of 400%'
          ],
          style: 'check'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'dm-p1', description: 'Monthly retainer - Full digital marketing management', quantity: 1, unitPrice: 5500, total: 5500 },
            { id: 'dm-p2', description: 'Setup fee - Strategy, configuration and system integration', quantity: 1, unitPrice: 3000, total: 3000 },
            { id: 'dm-p3', description: 'Recommended monthly ad budget', quantity: 1, unitPrice: 8000, total: 8000 }
          ],
          showQuantity: false,
          showUnitPrice: false
        }
      },
      {
        type: 'heading',
        data: { content: 'Reporting Format', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: '• Weekly report - Performance summary and key insights via email\n• Detailed monthly report - Comprehensive analysis, KPI comparison and recommendations\n• Monthly status meeting - Joint review and next month planning\n• Online dashboard - Real-time access to performance data',
          alignment: 'left'
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Engagement Terms',
          items: [
            'Minimum engagement period: 3 months',
            'Monthly payment in advance by the 5th of each month',
            'Cancellation: 30 days written notice required',
            'Ad budget: Paid directly to platforms or through us',
            'Proposal validity: 14 days from date of issue',
            'Reports and data: Client receives full access to all accounts and data'
          ],
          style: 'numbered',
          theme: 'payment',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Agency Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'real-estate',
    name: 'Real Estate',
    nameHe: 'Real Estate',
    description: 'Proposal for real estate services',
    descriptionHe: 'Proposal for brokerage and real estate services',
    category: 'industry',
    color: 'teal',
    icon: 'Building',
    coverImage: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Real Estate Services Proposal',
          subtitle: 'Professional guidance for your real estate deal',
          backgroundImage: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
          backgroundColor: '#0d9488',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'heading',
        data: { content: 'Property Details', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Property type: ________________\nAddress: ________________\nArea: ________ sqft\nFloor: ________ of ________\nNumber of rooms: ________\nParking: ☐ Yes  ☐ No\nStorage: ☐ Yes  ☐ No\nProperty condition: ________________\nYear built: ________________',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Market Analysis', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Based on analysis of recent transactions in the area and current market trends, here is our valuation:\n\nPrice range in neighborhood: $450,000 - $625,000\nAverage price per sqft in area: $280 - $350\nPrice trend: 5-8% increase in the past year\n\nEstimated property value: $____________\n\nThe valuation is based on comparable transactions, property condition, floor, orientation and improvements made.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Property Marketing Plan', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Professional property photography - Expert real estate photographer',
            'Video tour + 360° virtual walkthrough',
            'Listing on leading property portals',
            'Targeted social media campaign',
            'Exposure to our buyer network - Database of 3,000+ active buyers',
            'Property and area signage',
            'Open house days and tour coordination'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Commission & Terms', level: 2 }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 're-p1', description: 'Brokerage commission (1% + tax of deal price)', quantity: 1, unitPrice: 20000, total: 20000 },
            { id: 're-p2', description: 'Extended marketing package (optional)', quantity: 1, unitPrice: 2500, total: 2500 },
            { id: 're-p3', description: 'Professional photography + virtual tour', quantity: 1, unitPrice: 1500, total: 1500 }
          ],
          showQuantity: false,
          showUnitPrice: false
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Exclusivity Terms',
          items: [
            'Exclusivity period: 90 days from signing date',
            'During the exclusivity period, the agent will be the sole entity marketing and selling the property',
            'Commission is payable upon deal completion and sale contract signing',
            'Exclusivity cancellation: 14 days written notice, after the first 60 days',
            'The agent commits to reporting on marketing activity every two weeks',
            'Deals with buyers introduced by the agent - Commission applies even after exclusivity ends'
          ],
          style: 'numbered',
          theme: 'legal',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'timeline',
        data: {
          title: 'Estimated Timeline',
          items: [
            { id: 're-t1', title: 'Property Preparation', description: 'Photography, description, and marketing materials', date: 'Week 1' },
            { id: 're-t2', title: 'Marketing Launch', description: 'Listing on portals and social media', date: 'Week 2' },
            { id: 're-t3', title: 'Tours & Negotiations', description: 'Receiving offers and managing negotiations', date: 'Week 3-8' },
            { id: 're-t4', title: 'Closing the Deal', description: 'Contract signing and deal completion', date: 'Week 8-12' }
          ],
          layout: 'horizontal',
          color: '#0d9488'
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Agent Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'business-consulting',
    name: 'Business Consulting',
    nameHe: 'Business Consulting',
    description: 'Proposal for business consulting services',
    descriptionHe: 'Proposal for business and strategic consulting',
    category: 'professional',
    color: 'indigo',
    icon: 'Lightbulb',
    coverImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Business Consulting Proposal',
          subtitle: 'Proven methodology for business success',
          backgroundImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=600&fit=crop',
          backgroundColor: '#db2777',
          overlay: true,
          overlayOpacity: 0.65
        }
      },
      {
        type: 'heading',
        data: { content: 'Our Methodology', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'We apply a data-driven methodology developed from experience with hundreds of businesses. Our approach combines quantitative analysis with deep understanding of business dynamics, enabling us to identify growth opportunities and build effective action plans.',
          alignment: 'left'
        }
      },
      {
        type: 'timeline',
        data: {
          title: 'Project Phases',
          items: [
            { id: 'bc-t1', title: 'Diagnosis & Mapping', description: 'In-depth analysis of current state: finances, operations, marketing, HR', date: 'Week 1-2' },
            { id: 'bc-t2', title: 'Strategy Definition', description: 'Formulating vision, goals, and detailed strategic plan', date: 'Week 3-4' },
            { id: 'bc-t3', title: 'Action Plan', description: 'Detailing practical steps, timelines and owners', date: 'Week 5' },
            { id: 'bc-t4', title: 'Implementation Guidance', description: 'Close collaboration with the team for plan execution', date: 'Week 6-12' },
            { id: 'bc-t5', title: 'Measurement & Optimization', description: 'KPI tracking, adjustments and continuous improvement', date: 'Ongoing' }
          ],
          layout: 'vertical',
          color: '#db2777'
        }
      },
      {
        type: 'heading',
        data: { content: 'Deliverables', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Comprehensive diagnostic report - SWOT analysis, process mapping and organizational structure',
            'Strategy presentation - vision, goals and roadmap',
            'Updated business plan - including 3-year financial forecast',
            'Detailed action plan - tasks, timelines and responsible parties',
            'Monthly progress reports - KPI tracking and status',
            'Project summary and recommendations for next steps'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Our Team', level: 2 }
      },
      {
        type: 'team',
        data: {
          title: 'Project Team',
          members: [
            { id: 'bc-m1', name: 'Consulting Team Lead', role: 'Senior Strategic Consultant', description: '15+ years of business consulting experience in the private and public sectors' },
            { id: 'bc-m2', name: 'Business Analyst', role: 'Data Analysis & Financial Modeling', description: 'Expert in market analysis, forecasting and economic models' },
            { id: 'bc-m3', name: 'Operations Consultant', role: 'Process Optimization & Org Structure', description: 'Specializes in operational performance improvement and change management' }
          ],
          columns: 3
        }
      },
      {
        type: 'packages',
        data: {
          title: 'Consulting Tracks',
          packages: [
            {
              id: 'bc-focus',
              name: 'Focused Consulting',
              price: 8500,
              currency: '$',
              description: 'For a specific business issue',
              features: [
                'Focused diagnosis',
                'Recommendations report',
                '3 consulting sessions',
                'Action plan',
                'Phone support for one month'
              ]
            },
            {
              id: 'bc-standard',
              name: 'Comprehensive Consulting',
              price: 25000,
              currency: '$',
              description: 'Full business support',
              features: [
                'Comprehensive diagnosis',
                'Strategy & business plan',
                'Weekly meetings for 3 months',
                'Monthly progress reports',
                'Phone + email support',
                'Management team workshop'
              ],
              highlighted: true
            },
            {
              id: 'bc-premium',
              name: 'Strategic Partnership',
              price: 45000,
              currency: '$',
              description: 'Annual close support',
              features: [
                'Everything in comprehensive package',
                'Support for 12 months',
                'Board meeting participation',
                'Business network access',
                'Investor fundraising consulting',
                'External advisory board'
              ]
            }
          ]
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Engagement Terms',
          items: [
            'Payment: 40% deposit, 30% mid-project, 30% upon completion',
            'Confidentiality: Both parties commit to complete confidentiality',
            'Intellectual property: All deliverables belong to client upon full payment',
            'Cancellation: 14-day notice, pro-rata payment for work completed',
            'Proposal validity: 30 days from date of issue'
          ],
          style: 'numbered',
          theme: 'legal',
          variant: 'outlined',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Consultant Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'event-production',
    name: 'Event Production',
    nameHe: 'Event Production',
    description: 'Proposal for event production services',
    descriptionHe: 'Proposal for event production and management',
    category: 'industry',
    color: 'violet',
    icon: 'PartyPopper',
    coverImage: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Event Production Proposal',
          subtitle: 'An unforgettable experience from start to finish',
          backgroundImage: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200&h=600&fit=crop',
          backgroundColor: '#e14d9a',
          overlay: true,
          overlayOpacity: 0.55
        }
      },
      {
        type: 'heading',
        data: { content: 'Event Details', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Event type: ________________\nDate: ________________\nEstimated number of guests: ________________\nEvent style: ________________\nHours: ________________',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Venue Options', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Event hall - Suitable for 200+ guests, parking available, central A/C',
            'Garden venue - Rustic atmosphere, suitable for 150 guests, weather backup',
            'Urban loft - Modern and urban, suitable for 100 guests',
            'Private home / villa - Intimate and personal, up to 80 guests'
          ],
          style: 'bullet'
        }
      },
      {
        type: 'heading',
        data: { content: 'Catering & Menu', level: 2 }
      },
      {
        type: 'packages',
        data: {
          title: 'Catering Packages',
          packages: [
            {
              id: 'ev-catering-basic',
              name: 'Classic',
              price: 180,
              currency: '$',
              period: 'per guest',
              description: 'Quality varied menu',
              features: [
                'Reception with 5 dishes',
                'First course of choice',
                'Main course (meat/fish/poultry)',
                'Basic alcohol bar',
                'Desserts and event cake',
                'Professional waiter service'
              ]
            },
            {
              id: 'ev-catering-premium',
              name: 'Premium',
              price: 280,
              currency: '$',
              period: 'per guest',
              description: 'The ultimate culinary experience',
              features: [
                'Reception with 8 dishes',
                'Live food stations',
                'First course + intermediate course',
                'Double main course',
                'Premium alcohol bar',
                'Luxury dessert bar',
                'Chef at event',
                'Custom menu'
              ],
              highlighted: true
            }
          ]
        }
      },
      {
        type: 'heading',
        data: { content: 'Entertainment & Production', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'Production Services',
          items: [
            { icon: 'Music', text: 'Professional DJ / Live band - Matched to event style' },
            { icon: 'Camera', text: 'Stills + Video photography - Full event documentation' },
            { icon: 'Flower', text: 'Design & flowers - Table design, canopy, and venue decor' },
            { icon: 'Sparkles', text: 'Lighting & effects - Ambient lighting, lasers and fireworks' },
            { icon: 'Mic', text: 'MC / Ceremony DJ - Event management and timeline' },
            { icon: 'Car', text: 'Guest transportation - Shuttles from central locations' }
          ],
          columns: 2,
          iconColor: '#e14d9a'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'ev-p1', description: 'Full event production - Management & coordination', quantity: 1, unitPrice: 8000, total: 8000 },
            { id: 'ev-p2', description: 'Catering (200 guests x $280)', quantity: 200, unitPrice: 280, total: 56000 },
            { id: 'ev-p3', description: 'DJ + Sound & lighting system', quantity: 1, unitPrice: 5000, total: 5000 },
            { id: 'ev-p4', description: 'Stills + Video photography', quantity: 1, unitPrice: 7000, total: 7000 },
            { id: 'ev-p5', description: 'Design & flowers', quantity: 1, unitPrice: 6000, total: 6000 },
            { id: 'ev-p6', description: 'Event venue - Rental', quantity: 1, unitPrice: 15000, total: 15000 }
          ],
          showQuantity: false,
          showUnitPrice: false
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Terms & Commitments',
          items: [
            'Date reservation: 30% deposit of total proposal amount',
            'Second payment (40%): 30 days before the event',
            'Balance (30%): Up to 7 days before the event',
            'Cancellation up to 60 days before: Full refund minus $2,000 handling fee',
            'Cancellation 30-60 days before: 50% refund',
            'Cancellation less than 30 days: No refund',
            'Final guest count: To be submitted 7 days before the event',
            'Proposal validity: 14 days'
          ],
          style: 'numbered',
          theme: 'payment',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Producer Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'app-development',
    name: 'App Development',
    nameHe: 'App Development',
    description: 'Proposal for mobile app development',
    descriptionHe: 'Proposal for mobile app development',
    category: 'creative',
    color: 'sky',
    icon: 'Smartphone',
    coverImage: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'App Development Proposal',
          subtitle: 'From idea to successful digital product',
          backgroundImage: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=1200&h=600&fit=crop',
          backgroundColor: '#0284c7',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'heading',
        data: { content: 'App Concept', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'The proposed app will provide a comprehensive solution for ________________. The app will be available on iOS and Android with an intuitive user interface, fast performance and the highest level of data security.\n\nDevelopment will be based on React Native / Flutter technology (per our recommendation) enabling development for both platforms simultaneously with significant cost savings.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Feature List', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'Core Capabilities',
          items: [
            { icon: 'User', text: 'User system - Registration, login, profile and authentication' },
            { icon: 'Bell', text: 'Push notifications - Real-time updates for users' },
            { icon: 'CreditCard', text: 'Payment system - Secure billing and in-app payments' },
            { icon: 'Search', text: 'Advanced search & filtering - Fast and accurate search engine' },
            { icon: 'MessageCircle', text: 'Chat & messaging - Real-time communication between users' },
            { icon: 'BarChart', text: 'Dashboard & analytics - Statistics and reports for admins' },
            { icon: 'Globe', text: 'Multi-language support - English, Spanish and more' },
            { icon: 'Shield', text: 'Data security - Encryption, GDPR compliance' }
          ],
          columns: 2,
          iconColor: '#0284c7'
        }
      },
      {
        type: 'heading',
        data: { content: 'Technology Stack', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Frontend: React Native / Flutter - Cross-platform development',
            'Backend: Node.js + Express / Firebase',
            'Database: PostgreSQL + Redis for caching',
            'Cloud hosting: AWS / Google Cloud Platform',
            'CI/CD: GitHub Actions + Fastlane',
            'Payment system: Stripe / PayPlus'
          ],
          style: 'bullet'
        }
      },
      {
        type: 'timeline',
        data: {
          title: 'Development Phases',
          items: [
            { id: 'app-t1', title: 'Detailed Specification', description: 'User stories, wireframes, technical architecture', date: 'Week 1-2' },
            { id: 'app-t2', title: 'UI/UX Design', description: 'Screen design, interactive prototype', date: 'Week 3-4' },
            { id: 'app-t3', title: 'MVP Development', description: 'First version development with core features', date: 'Week 5-10' },
            { id: 'app-t4', title: 'QA Testing', description: 'Functional, performance and security testing', date: 'Week 11-12' },
            { id: 'app-t5', title: 'Store Launch', description: 'Submission to App Store and Google Play', date: 'Week 13' }
          ],
          layout: 'vertical',
          color: '#0284c7'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'app-p1', description: 'Detailed specification & UX', quantity: 1, unitPrice: 12000, total: 12000 },
            { id: 'app-p2', description: 'UI Design - All screens', quantity: 1, unitPrice: 15000, total: 15000 },
            { id: 'app-p3', description: 'Frontend development (iOS + Android)', quantity: 1, unitPrice: 45000, total: 45000 },
            { id: 'app-p4', description: 'Backend development + API', quantity: 1, unitPrice: 25000, total: 25000 },
            { id: 'app-p5', description: 'QA Testing & Launch', quantity: 1, unitPrice: 8000, total: 8000 }
          ],
          showQuantity: false,
          showUnitPrice: false
        }
      },
      {
        type: 'heading',
        data: { content: 'Maintenance Plan', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'After launch, we offer a monthly maintenance plan that includes:\n\n• Bug fixes and security updates - $3,500/month\n• Updates for new operating systems\n• Daily backups and 24/7 monitoring\n• Up to 10 monthly development hours for minor improvements\n• Response time: Up to 4 hours for critical issues',
          alignment: 'left'
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Project Terms',
          items: [
            'Payment: 30% deposit, 30% upon spec completion, 30% upon dev completion, 10% at launch',
            'Source code rights: Transfer to client upon full payment',
            'Warranty: 3-month bug warranty after launch',
            'Scope changes: Any material change will be priced separately and approved in writing',
            'Timelines: Client-side delays (approvals, content) will extend the project accordingly',
            'Proposal validity: 30 days'
          ],
          style: 'numbered',
          theme: 'general',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Development Company Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'legal-services',
    name: 'Legal Services',
    nameHe: 'Legal Services',
    description: 'Proposal for legal services',
    descriptionHe: 'Proposal for legal services',
    category: 'professional',
    color: 'stone',
    icon: 'Scale',
    coverImage: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Legal Services Proposal',
          subtitle: 'Professional and reliable legal counsel',
          backgroundImage: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
          backgroundColor: '#44403c',
          overlay: true,
          overlayOpacity: 0.7
        }
      },
      {
        type: 'heading',
        data: { content: 'About the Firm', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Our firm specializes in providing comprehensive legal services to private and business clients. We bring years of experience, full commitment to our clients and uncompromising professionalism.\n\nThe firm has an experienced team of attorneys in civil, commercial, real estate, labor law and criminal law.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Service Description', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Based on our meeting, the following legal services are proposed:',
          alignment: 'left'
        }
      },
      {
        type: 'list',
        data: {
          items: [
            'Ongoing legal counsel - Answering questions and legal guidance',
            'Contract drafting & agreements - Writing, editing and reviewing',
            'Legal representation - Courts and judicial bodies',
            'Due Diligence - Before transactions',
            'Real estate transaction support - Sale, purchase, leasing',
            'Labor law consulting - Employment contracts, terminations, rights'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Service Scope & Pricing', level: 2 }
      },
      {
        type: 'packages',
        data: {
          title: 'Service Plans',
          packages: [
            {
              id: 'legal-hourly',
              name: 'Hourly Consulting',
              price: 650,
              currency: '$',
              period: 'per hour',
              description: 'For specific needs',
              features: [
                'Fixed hourly rate',
                'Detailed time reporting',
                'Available on business days',
                'Minimum charge: 30 minutes'
              ]
            },
            {
              id: 'legal-retainer',
              name: 'Monthly Retainer',
              price: 4500,
              currency: '$',
              period: 'per month',
              description: 'Ongoing legal support',
              features: [
                'Up to 8 consulting hours per month',
                'Priority response - up to 24 hours',
                'Ongoing contract review',
                'Phone and email consulting',
                'Additional hours at reduced rate ($500/hour)'
              ],
              highlighted: true
            },
            {
              id: 'legal-project',
              name: 'Fixed Project',
              price: 15000,
              currency: '$',
              description: 'Fixed price for a defined project',
              features: [
                'Pre-agreed fixed price',
                'Defined timeline',
                'All services until project completion',
                'No billing surprises',
                'Weekly progress reports'
              ]
            }
          ]
        }
      },
      {
        type: 'heading',
        data: { content: 'Billing Terms', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Invoice issued on the 1st of each month for the previous month',
            'Payment: Net 15 from invoice date',
            'Additional expenses: Court fees, travel and postage charged separately',
            'Initial deposit of $5,000 due upon signing the agreement'
          ],
          style: 'numbered'
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Confidentiality & Privilege',
          items: [
            'Attorney-client privilege: All information provided shall be confidential under law',
            'The firm commits to complete confidentiality of all business and personal information',
            'No conflict of interest: The firm confirms no known conflict of interest exists',
            'In case of future conflict, the client will be notified immediately and a solution offered',
            'Termination: Either party may terminate with 30-day written notice',
            'Upon termination, all documents will be returned to the client'
          ],
          style: 'numbered',
          theme: 'privacy',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Attorney Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'education-training',
    name: 'Education & Training',
    nameHe: 'Education & Training',
    description: 'Proposal for education and training services',
    descriptionHe: 'Proposal for training and certification services',
    category: 'industry',
    color: 'lime',
    icon: 'GraduationCap',
    coverImage: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Professional Training Program',
          subtitle: 'Learning that drives results',
          backgroundImage: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1200&h=600&fit=crop',
          backgroundColor: '#65a30d',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'heading',
        data: { content: 'Program Overview', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'The proposed program is designed to provide practical and professional tools in the field of ________________. The program was developed based on extensive experience training hundreds of participants and is tailored to the specific needs of your organization.\n\nOur pedagogical model combines theoretical learning with hands-on practice, group discussions and applied projects.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Curriculum', level: 2 }
      },
      {
        type: 'timeline',
        data: {
          title: 'Course Map',
          items: [
            { id: 'edu-t1', title: 'Module 1: Foundations', description: 'Introduction to the field, key concepts, market overview and trends', date: 'Week 1-2' },
            { id: 'edu-t2', title: 'Module 2: Tools & Methodologies', description: 'Learning the key tools and common work methods', date: 'Week 3-4' },
            { id: 'edu-t3', title: 'Module 3: Practical Application', description: 'Workshops, exercises and real-world simulations', date: 'Week 5-6' },
            { id: 'edu-t4', title: 'Module 4: Final Project', description: 'Work on a real project from the organization + presentation', date: 'Week 7-8' }
          ],
          layout: 'vertical',
          color: '#65a30d'
        }
      },
      {
        type: 'heading',
        data: { content: 'What the Course Includes', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            '32 academic hours (8 sessions of 4 hours)',
            'Printed and digital study materials',
            'Access to online learning platform for one year',
            'Exercises and homework between sessions',
            'Personal instructor support via email and phone',
            'Recognized completion certificate from the institute',
            'Coffee, pastries and light refreshments at sessions'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'The Instructor', level: 2 }
      },
      {
        type: 'team',
        data: {
          title: 'Teaching Team',
          members: [
            { id: 'edu-m1', name: 'Lead Instructor', role: 'Subject Matter Expert', description: '10+ years of industry experience and 5+ years in teaching and training. Senior academic lecturer.' }
          ],
          columns: 2
        }
      },
      {
        type: 'packages',
        data: {
          title: 'Training Plans',
          packages: [
            {
              id: 'edu-individual',
              name: 'Individual',
              price: 3200,
              currency: '$',
              description: 'Personal course registration',
              features: [
                'Participation in all sessions',
                'Full study materials',
                'Online platform access',
                'Completion certificate'
              ]
            },
            {
              id: 'edu-group',
              name: 'Group (5-10)',
              price: 2600,
              currency: '$',
              period: 'per participant',
              description: 'Discounted group rate',
              features: [
                'Everything in individual plan',
                '19% discount per participant',
                'Examples tailored to organization field',
                'Executive summary for referrer'
              ],
              highlighted: true
            },
            {
              id: 'edu-org',
              name: 'Corporate (10+)',
              price: 28000,
              currency: '$',
              description: 'Dedicated course for organization',
              features: [
                'Fully customized course for organization',
                'Up to 20 participants',
                'Location: Your office or ours',
                'Flexible scheduling',
                'Final project on organizational case study',
                'Post-course support for 3 months'
              ]
            }
          ]
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Registration Terms',
          items: [
            'Registration requires a $500 deposit',
            'Cancellation up to 14 days before course start: Full refund',
            'Cancellation 7-14 days before: 50% refund',
            'Cancellation less than 7 days: Spot can be transferred to another participant',
            'Course opening conditional on minimum 8 registrations',
            'In case of cancellation by us: Full refund or transfer to next cohort'
          ],
          style: 'numbered',
          theme: 'cancellation',
          variant: 'outlined',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Training Institute Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'catering',
    name: 'Catering',
    nameHe: 'Catering Services',
    description: 'Proposal for catering services',
    descriptionHe: 'Proposal for catering and hospitality services',
    category: 'industry',
    color: 'red',
    icon: 'UtensilsCrossed',
    coverImage: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Catering Services Proposal',
          subtitle: 'Flavors that speak for themselves',
          backgroundImage: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=1200&h=600&fit=crop',
          backgroundColor: '#dc2626',
          overlay: true,
          overlayOpacity: 0.55
        }
      },
      {
        type: 'heading',
        data: { content: 'Proposed Menu', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'The proposed menu was prepared according to your request and tailored to the nature of the event. All dishes are freshly prepared on the day of the event using quality ingredients.\n\nDietary: ☐ Kosher  ☐ Strictly Kosher  ☐ None\nMenu type: ☐ Meat  ☐ Dairy  ☐ Fish  ☐ Mixed',
          alignment: 'left'
        }
      },
      {
        type: 'packages',
        data: {
          title: 'Catering Packages',
          packages: [
            {
              id: 'cat-basic',
              name: 'Classic Package',
              price: 120,
              currency: '$',
              period: 'per diner',
              description: 'Perfect for corporate events',
              features: [
                'Breads & dips (5 types)',
                'Salads (4 types)',
                'Main course of choice',
                'Sides (rice/pasta/potatoes)',
                'Soft drinks',
                'Individual dessert'
              ]
            },
            {
              id: 'cat-premium',
              name: 'Deluxe Package',
              price: 180,
              currency: '$',
              period: 'per diner',
              description: 'For special events',
              features: [
                'Assorted breads & dips (8 types)',
                'Chef salads (6 types)',
                'Served first course',
                'Double main course',
                'Premium sides',
                'Hot & cold beverage bar',
                'Dessert bar',
                'Table flowers'
              ],
              highlighted: true
            },
            {
              id: 'cat-vip',
              name: 'VIP',
              price: 250,
              currency: '$',
              period: 'per diner',
              description: 'The ultimate culinary experience',
              features: [
                'Everything in Deluxe package',
                'Live food stations',
                'Chef at event',
                'Fully custom menu',
                'Premium alcohol bar',
                'Full table design',
                'Extended waiter team'
              ]
            }
          ]
        }
      },
      {
        type: 'heading',
        data: { content: 'Dietary Accommodations', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'We accommodate every requirement',
          items: [
            { icon: 'Leaf', text: 'Vegan / Vegetarian - Full and varied dishes without animal products' },
            { icon: 'Shield', text: 'Gluten-free - Menu adapted for gluten sensitivity and celiac' },
            { icon: 'Heart', text: 'Allergies - Accommodation for all types of allergies and sensitivities' },
            { icon: 'Star', text: 'Diet - Reduced calorie dishes without compromising taste' }
          ],
          columns: 2,
          iconColor: '#dc2626'
        }
      },
      {
        type: 'heading',
        data: { content: 'Service Team', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Event manager - Responsible for operations from A to Z',
            'Head chef - Overseeing preparation of all dishes',
            'Professional waiters - 1:15 ratio (one waiter per 15 diners)',
            'Bar team - Skilled bartenders for beverage service',
            'Cleanup crew - Ongoing cleaning throughout and after the event'
          ],
          style: 'check'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'cat-p1', description: 'Deluxe catering package (100 diners)', quantity: 100, unitPrice: 180, total: 18000 },
            { id: 'cat-p2', description: 'Service team (waiters, bartenders)', quantity: 1, unitPrice: 3000, total: 3000 },
            { id: 'cat-p3', description: 'Equipment & transport (utensils, tables, chairs)', quantity: 1, unitPrice: 2500, total: 2500 },
            { id: 'cat-p4', description: 'Table design & linens', quantity: 1, unitPrice: 1500, total: 1500 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Terms',
          items: [
            'Final guest count: Must be provided at least 5 business days before the event',
            'Minimum charge: Based on 90% of the provided guest count',
            'Payment: 50% deposit with booking, 50% up to 3 days before event',
            'Tasting: Offered at no charge for events over 80 diners',
            'Cancellation up to 14 days before: Full refund less $500',
            'Cancellation less than 14 days: No refund on deposit',
            'Certification: Kosher certificate provided upon request',
            'Proposal valid for: 14 days'
          ],
          style: 'numbered',
          theme: 'payment',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Catering Company Signature',
          showDate: true
        }
      }
    ])
  },
  {
    id: 'it-services',
    name: 'IT Services',
    nameHe: 'IT Services',
    description: 'Proposal for IT infrastructure and support services',
    descriptionHe: 'Proposal for IT infrastructure services and technical support',
    category: 'industry',
    color: 'green',
    icon: 'Server',
    coverImage: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'IT Services Proposal',
          subtitle: 'Technology infrastructure that drives your business',
          backgroundImage: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&h=600&fit=crop',
          backgroundColor: '#16a34a',
          overlay: true,
          overlayOpacity: 0.65
        }
      },
      {
        type: 'heading',
        data: { content: 'Infrastructure Overview', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Based on the survey we conducted at your organization, here are our findings and proposal for upgrading and maintaining your IT infrastructure. The proposed solution was built considering the current and future needs of the organization, with emphasis on data security, high availability, and scalability.',
          alignment: 'left'
        }
      },
      {
        type: 'stats',
        data: {
          title: '',
          items: [
            { id: 'it-s1', value: '99.9%', label: 'Guaranteed Uptime' },
            { id: 'it-s2', value: '< 2 hrs', label: 'Response Time' },
            { id: 'it-s3', value: '24/7', label: 'Monitoring & Support' },
            { id: 'it-s4', value: '200+', label: 'Business Clients' }
          ],
          columns: 4,
          color: '#16a34a'
        }
      },
      {
        type: 'heading',
        data: { content: 'Proposed Solutions', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'Comprehensive IT Services',
          items: [
            { icon: 'Server', text: 'Servers & Infrastructure - Setup, maintenance and management of physical and cloud servers' },
            { icon: 'Shield', text: 'Data Security - Firewall, antivirus, backups and cyber protection' },
            { icon: 'Wifi', text: 'Communications & Networks - Network infrastructure, VPN, enterprise Wi-Fi' },
            { icon: 'Cloud', text: 'Cloud Services - Microsoft 365, Google Workspace, AWS/Azure' },
            { icon: 'HardDrive', text: 'Backup & Recovery - Automatic backups, DR plan' },
            { icon: 'Headphones', text: 'Technical Support - Help Desk, remote and on-site support' }
          ],
          columns: 2,
          iconColor: '#16a34a'
        }
      },
      {
        type: 'heading',
        data: { content: 'Service Levels (SLA)', level: 2 }
      },
      {
        type: 'packages',
        data: {
          title: 'Support Plans',
          packages: [
            {
              id: 'it-basic',
              name: 'Basic',
              price: 2500,
              currency: '$',
              period: 'per month',
              description: 'For small businesses (up to 10 stations)',
              features: [
                'Support Sun-Thu, 9:00-18:00',
                'Response time: up to 4 hours',
                'Remote support only',
                'Basic monitoring',
                'Daily backup',
                'Monthly security updates'
              ]
            },
            {
              id: 'it-business',
              name: 'Business',
              price: 5500,
              currency: '$',
              period: 'per month',
              description: 'For medium businesses (10-50 stations)',
              features: [
                'Support Sun-Thu 8:00-20:00, Fri 8:00-13:00',
                'Response time: up to 2 hours',
                'Remote support + monthly on-site visit',
                '24/7 monitoring with alerts',
                'Daily + weekly offsite backup',
                'Weekly security updates',
                'Detailed monthly report'
              ],
              highlighted: true
            },
            {
              id: 'it-enterprise',
              name: 'Enterprise',
              price: 12000,
              currency: '$',
              period: 'per month',
              description: 'For organizations (50+ stations)',
              features: [
                '24/7 support including holidays',
                'Response time: up to 1 hour',
                'Dedicated technician + weekly visits',
                'Advanced monitoring + SOC',
                'Real-time backup + DR',
                'Virtual CISO',
                'Guaranteed 99.9% SLA',
                'Weekly + quarterly reports'
              ]
            }
          ]
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'it-p1', description: 'Setup & infrastructure survey', quantity: 1, unitPrice: 5000, total: 5000 },
            { id: 'it-p2', description: 'Business plan - monthly service fee', quantity: 12, unitPrice: 5500, total: 66000 },
            { id: 'it-p3', description: 'Security software licensing (annual)', quantity: 1, unitPrice: 8000, total: 8000 },
            { id: 'it-p4', description: 'Hardware upgrade (as needed)', quantity: 1, unitPrice: 15000, total: 15000 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Security & SLA Terms',
          items: [
            'Guaranteed availability: 99.9% uptime for critical services',
            'Critical issue response time: According to selected plan',
            'Backups: Tested and verified monthly',
            'Data security: ISO 27001 compliance',
            'Confidentiality: Full NDA on all organizational data',
            'Contract period: 12 months, auto-renewal',
            'Cancellation: 60 days written notice in advance',
            'SLA breach: 10% compensation of monthly fee per hour of excess'
          ],
          style: 'numbered',
          theme: 'warning',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'IT Provider Signature',
          showDate: true
        }
      }
    ])
  },

  // ===== ADDITIONAL INDUSTRY TEMPLATES (Batch 2) =====

  {
    id: 'architecture-interior',
    name: 'Architecture & Interior Design',
    nameHe: 'Architecture & Interior Design',
    description: 'Proposal for architecture and interior design projects',
    descriptionHe: 'Proposal for architecture and interior design projects',
    category: 'industry',
    color: 'stone',
    icon: 'Home',
    coverImage: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Interior Design & Architecture Proposal',
          subtitle: 'Spaces that tell your story',
          backgroundImage: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1200&h=600&fit=crop',
          backgroundColor: '#e11d48',
          overlay: true,
          overlayOpacity: 0.5
        }
      },
      {
        type: 'heading',
        data: { content: 'Design Vision', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'We believe every space holds unique potential. Following our initial meeting and property tour, we have crafted a design concept that combines maximum functionality with contemporary aesthetics. The proposed design takes into account your lifestyle, personal preferences, and the unique characteristics of the building.\n\nOur approach combines innovation with respect for natural materials, using advanced technologies and green solutions.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Inspiration Board', level: 2 }
      },
      {
        type: 'gallery',
        data: {
          images: [
            { src: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=400&h=300&fit=crop', alt: 'Modern living room' },
            { src: 'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=400&h=300&fit=crop', alt: 'Designed kitchen' },
            { src: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&h=300&fit=crop', alt: 'Bedroom' },
            { src: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=300&fit=crop', alt: 'Bathroom' },
            { src: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=300&fit=crop', alt: 'Workspace' },
            { src: 'https://images.unsplash.com/photo-1600566753376-12c8ab7c5a38?w=400&h=300&fit=crop', alt: 'Balcony' }
          ],
          columns: 3
        }
      },
      {
        type: 'heading',
        data: { content: 'Room-by-Room Work Plan', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'Scope of Work',
          items: [
            { icon: 'Sofa', text: 'Living room & dining area - Full design including furniture, lighting, textiles and decorative elements' },
            { icon: 'ChefHat', text: 'Kitchen - Cabinet planning, countertop selection, smart storage solutions and functional lighting' },
            { icon: 'Bed', text: 'Bedrooms (3) - Intimate design, built-in closets, lighting and custom beds' },
            { icon: 'Bath', text: 'Bathrooms (2) - Tile selection, sanitary fixtures, cabinets and bath accessories' },
            { icon: 'Lamp', text: 'Balcony & entrance - Exterior design, planters, garden lighting and seating area' }
          ],
          columns: 1,
          iconColor: '#e11d48'
        }
      },
      {
        type: 'heading',
        data: { content: 'Materials & Finishes', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Material selection is made with a commitment to quality and durability over the years:\n\n• Flooring: Porcelain granite tiles with natural finish, 60x120 cm — imported from Italy\n• Kitchen countertops: 3 cm quartz composite in light tone, heat and scratch resistant\n• Wall paint: Jotun premium paint in matte finish, neutral color palette with accents\n• Wood: Natural Liechtenstein oak for furniture and hardware\n• Lighting: Fixtures from leading brands - Flos, Artemide and local designers',
          alignment: 'left'
        }
      },
      {
        type: 'timeline',
        data: {
          title: 'Timeline',
          items: [
            { id: 'arch-t1', title: 'Phase 1: Planning & Design', description: 'Coordination meetings, plan preparation, material selection & 3D', date: 'Weeks 1-4' },
            { id: 'arch-t2', title: 'Phase 2: Specifications', description: 'Technical drawings, execution specs and material ordering', date: 'Weeks 5-6' },
            { id: 'arch-t3', title: 'Phase 3: Execution', description: 'Work supervision, contractor coordination and quality control', date: 'Weeks 7-14' },
            { id: 'arch-t4', title: 'Phase 4: Styling & Handover', description: 'Furniture installation, accessories, final styling and handover', date: 'Weeks 15-16' }
          ],
          layout: 'vertical',
          color: '#e11d48'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'arch-p1', description: 'Architectural planning & interior design - including 3D', quantity: 1, unitPrice: 25000, total: 25000 },
            { id: 'arch-p2', description: 'Purchase support - store visits and furniture selection', quantity: 1, unitPrice: 5000, total: 5000 },
            { id: 'arch-p3', description: 'Senior supervision of execution', quantity: 1, unitPrice: 12000, total: 12000 },
            { id: 'arch-p4', description: 'Styling & accessory design', quantity: 1, unitPrice: 4000, total: 4000 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Terms & Agreements',
          items: [
            'Payment in 4 stages: 30% at signing, 30% at plan approval, 25% at start of execution, 15% at handover',
            'Significant changes to the plan after planning phase approval will incur a 10%-20% surcharge',
            'The client is responsible for purchasing furniture and materials — the designer guides and advises',
            'Senior supervision includes weekly site visits, close supervision at additional cost',
            'Proposal valid for: 30 days from submission date',
            'Copyright on the design belongs to the design firm'
          ],
          style: 'numbered',
          theme: 'legal',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Designer Signature',
          showDate: true
        }
      }
    ])
  },

  {
    id: 'insurance-services',
    name: 'Insurance',
    nameHe: 'Insurance',
    description: 'Insurance proposal with coverage plans and premium comparison',
    descriptionHe: 'Insurance proposal with coverage and premium comparison',
    category: 'industry',
    color: 'teal',
    icon: 'Shield',
    coverImage: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Insurance Plan Proposal',
          subtitle: 'Comprehensive protection for peace of mind',
          backgroundImage: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
          backgroundColor: '#0d9488',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'heading',
        data: { content: 'Insurance Coverage Overview', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'After a thorough review of your needs, we have prepared a comprehensive and personalized insurance plan. The plan was built with an understanding of your family, financial, and health situation, aiming to provide optimal protection at a fair price.\n\nWe work with the leading insurance companies and offer you the best terms on the market.',
          alignment: 'left'
        }
      },
      {
        type: 'stats',
        data: {
          title: '',
          items: [
            { id: 'ins-s1', value: '15+', label: 'Years of Experience' },
            { id: 'ins-s2', value: '98%', label: 'Claims Approved' },
            { id: 'ins-s3', value: '5,000+', label: 'Insured Clients' },
            { id: 'ins-s4', value: '12', label: 'Partner Insurance Companies' }
          ],
          columns: 4,
          color: '#0d9488'
        }
      },
      {
        type: 'heading',
        data: { content: 'Recommended Insurance Packages', level: 2 }
      },
      {
        type: 'packages',
        data: {
          title: 'Plan Comparison',
          packages: [
            {
              id: 'ins-basic',
              name: 'Basic',
              price: 350,
              currency: '$',
              period: 'per month',
              description: 'Essential coverage',
              features: [
                'Life insurance - $500,000 coverage',
                'Health insurance - Surgery coverage domestic',
                'Disability insurance - 75% of salary',
                'Personal accidents - Basic coverage',
                'Long-term care insurance - Standard plan'
              ]
            },
            {
              id: 'ins-recommended',
              name: 'Recommended',
              price: 650,
              currency: '$',
              period: 'per month',
              description: 'Most popular coverage',
              features: [
                'Life insurance - $1,000,000 coverage',
                'Health insurance - Surgery + dental + medications',
                'Disability insurance - 75% of salary, short waiting period',
                'Personal accidents - Extended coverage',
                'Long-term care insurance - Extended plan',
                'Critical illness insurance - $300,000',
                'Premium waiver on disability'
              ],
              highlighted: true
            },
            {
              id: 'ins-premium',
              name: 'Premium',
              price: 1100,
              currency: '$',
              period: 'per month',
              description: 'Maximum protection',
              features: [
                'Life insurance - $2,000,000 coverage',
                'Health insurance - Full coverage including abroad',
                'Disability insurance - 75% with no waiting period',
                'Personal accidents - Full coverage',
                'Long-term care insurance - VIP plan',
                'Critical illness insurance - $500,000',
                'Annual travel insurance',
                'Medical consultation 24/7'
              ]
            }
          ]
        }
      },
      {
        type: 'heading',
        data: { content: 'Policy Advantages', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'Why Choose Us',
          items: [
            { icon: 'Shield', text: 'Personal claims support - We stand by you every step' },
            { icon: 'Clock', text: 'Fast claims processing - Up to 14 business days' },
            { icon: 'Users', text: 'Family extension - Discounts up to 25% for immediate family' },
            { icon: 'TrendingDown', text: 'No-claims discount - 5% annual premium reduction' },
            { icon: 'Phone', text: '24/7 service center - Available at any time, every day' },
            { icon: 'RefreshCw', text: 'Annual review - Plan updates based on life changes' }
          ],
          columns: 2,
          iconColor: '#0d9488'
        }
      },
      {
        type: 'heading',
        data: { content: 'Key Exclusions', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Pre-existing medical conditions - Subject to medical underwriting',
            'Extreme sports and hazardous activities - Extension available separately',
            'Intentional self-harm or illegal activity',
            'Waiting period: 90 days for existing conditions, no waiting for accidents',
            'Cosmetic and elective treatments not medically necessary'
          ],
          style: 'bullet'
        }
      },
      {
        type: 'terms',
        data: {
          title: 'General Terms',
          items: [
            'The policy takes effect upon signing and payment of the first premium',
            'The policy may be cancelled at any time with 15 days advance notice',
            'The premium may change based on age and insurance history',
            'Claims must be filed within 90 days of the qualifying event',
            'The proposal is subject to underwriting approval by the insurance company',
            'Proposal valid for: 14 days from date of issue'
          ],
          style: 'numbered',
          theme: 'legal',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Insured Signature',
          leftLabel: 'Insurance Agent Signature',
          showDate: true
        }
      }
    ])
  },

  {
    id: 'accounting-tax',
    name: 'Accounting & Tax',
    nameHe: 'Accounting & Tax Consulting',
    description: 'Proposal for accounting, bookkeeping and tax consulting services',
    descriptionHe: 'Proposal for bookkeeping and tax consulting services',
    category: 'industry',
    color: 'cyan',
    icon: 'Calculator',
    coverImage: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Accounting & Tax Consulting Proposal',
          subtitle: 'Smart financial management for your business',
          backgroundImage: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
          backgroundColor: '#0891b2',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'heading',
        data: { content: 'Scope of Services', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Our firm offers a full range of services in bookkeeping, accounting, and tax consulting. With over 20 years of experience supporting small and medium businesses, we understand your financial challenges and offer tailored solutions that save you time and money.\n\nWe stay current with all legislative and regulatory changes to ensure full compliance and maximum utilization of tax benefits.',
          alignment: 'left'
        }
      },
      {
        type: 'benefits',
        data: {
          title: 'Our Services',
          items: [
            { icon: 'BookOpen', text: 'Ongoing bookkeeping - Transaction recording, bank reconciliations, ledger management' },
            { icon: 'FileText', text: 'Regulatory reporting - VAT returns, income tax, social security' },
            { icon: 'TrendingUp', text: 'Financial statements - Balance sheet, P&L, cash flow' },
            { icon: 'Calculator', text: 'Tax planning - Strategies for tax savings and benefit optimization' },
            { icon: 'Users', text: 'Payroll & HR - Pay slip generation, pension contributions, tax forms' },
            { icon: 'Scale', text: 'Authority representation - Income tax, VAT, social security' }
          ],
          columns: 2,
          iconColor: '#0891b2'
        }
      },
      {
        type: 'heading',
        data: { content: 'Service Packages', level: 2 }
      },
      {
        type: 'packages',
        data: {
          title: 'Accounting Support Plans',
          packages: [
            {
              id: 'acc-basic',
              name: 'Sole Proprietor',
              price: 750,
              currency: '$',
              period: 'per month',
              description: 'For freelancers and small businesses',
              features: [
                'Single-entry bookkeeping',
                'Bi-monthly VAT returns',
                'Annual income tax return',
                'Basic phone consulting',
                'Up to 80 transactions per month'
              ]
            },
            {
              id: 'acc-business',
              name: 'Medium Business',
              price: 2500,
              currency: '$',
              period: 'per month',
              description: 'For LLCs and growing businesses',
              features: [
                'Full double-entry bookkeeping',
                'VAT + withholding reports',
                'Annual report + financial statements',
                'Payroll processing (up to 10 employees)',
                'Ongoing tax consulting',
                'Tax authority representation',
                'Up to 300 transactions per month'
              ],
              highlighted: true
            },
            {
              id: 'acc-premium',
              name: 'Large Business',
              price: 5500,
              currency: '$',
              period: 'per month',
              description: 'Full financial support',
              features: [
                'All Medium Business plan services',
                'Unlimited employee payroll',
                'Quarterly management reports',
                'Strategic tax planning',
                'Capital raising & investor support',
                'External CFO - Monthly meeting',
                'Up to 1,000 transactions per month',
                'Direct availability to CEO'
              ]
            }
          ]
        }
      },
      {
        type: 'heading',
        data: { content: 'Monthly Deliverables', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Monthly trial balance - By the 15th of the month',
            'Monthly P&L report - By the 15th of the month',
            'Bank & vendor reconciliations - Ongoing',
            'VAT report - By the 15th of every odd month',
            'Withholding report - By the 15th of the month',
            'Payroll contributions report - By the 9th of the month',
            'Cash flow report - Quarterly (Medium plan and above)'
          ],
          style: 'check'
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Engagement Terms',
          items: [
            'Minimum engagement period: 12 months',
            'Monthly payment by the 10th of each month, via standing order',
            'Materials and documents to be submitted by the 5th of each month',
            'Exceptional work (audits, mergers, etc.) - Priced separately',
            'Full confidentiality on all business and financial information',
            'Cancellation with 60 days advance notice',
            'Proposal valid for: 30 days'
          ],
          style: 'numbered',
          theme: 'payment',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Accountant Signature',
          showDate: true
        }
      }
    ])
  },

  {
    id: 'music-production',
    name: 'Music & Production',
    nameHe: 'Music & Production',
    description: 'Proposal for music production, recording and mixing services',
    descriptionHe: 'Proposal for music production, recording and mixing services',
    category: 'industry',
    color: 'lime',
    icon: 'Music',
    coverImage: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Music Production Proposal',
          subtitle: 'The sound that tells your story',
          backgroundImage: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1200&h=600&fit=crop',
          backgroundColor: '#65a30d',
          overlay: true,
          overlayOpacity: 0.55
        }
      },
      {
        type: 'heading',
        data: { content: 'Our Work', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Our studio offers an end-to-end production experience — from songwriting to final mastering. With top-of-the-line equipment, perfect acoustics, and over 10 years of experience in the music industry, we bring every project to the highest level.\n\nWe have worked with leading artists, production companies, advertising agencies, and content creators — and would love to work with you too.',
          alignment: 'left'
        }
      },
      {
        type: 'gallery',
        data: {
          images: [
            { src: 'https://images.unsplash.com/photo-1598653222000-6b7b7a552625?w=400&h=300&fit=crop', alt: 'Recording studio' },
            { src: 'https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=400&h=300&fit=crop', alt: 'Mixing room' },
            { src: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=400&h=300&fit=crop', alt: 'Musical instruments' },
            { src: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=400&h=300&fit=crop', alt: 'Vocal recording' }
          ],
          columns: 2
        }
      },
      {
        type: 'heading',
        data: { content: 'Production Process', level: 2 }
      },
      {
        type: 'timeline',
        data: {
          title: 'Work Phases',
          items: [
            { id: 'music-t1', title: 'Pre-Production', description: 'Choosing musical direction, writing, initial arrangement and demos', date: 'Week 1-2' },
            { id: 'music-t2', title: 'Recording', description: 'Recording live instruments, vocals, backing vocals and sound design', date: 'Week 3-4' },
            { id: 'music-t3', title: 'Mixing', description: 'Editing, mixing, level balancing, effects and sound processing', date: 'Week 5' },
            { id: 'music-t4', title: 'Mastering & Delivery', description: 'Professional mastering, preparation for digital and physical distribution', date: 'Week 6' }
          ],
          layout: 'vertical',
          color: '#65a30d'
        }
      },
      {
        type: 'heading',
        data: { content: 'Deliverables', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Final master file in WAV format (24bit/48kHz)',
            'High quality MP3 version (320kbps) for digital distribution',
            'Stems - Separated channels for each instrument',
            'Additional versions as needed (acoustic, radio edit, karaoke)',
            'Original project files (if agreed upon)',
            'Full metadata for distribution on digital platforms'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Rights & Licensing', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Copyright on the melody, lyrics, and arrangement will be shared as agreed between the parties. In the case of commissioned production, all rights will be transferred to the client upon completion of payment.\n\nUse of music for synchronization purposes (advertising, film, television) — requires a separate licensing agreement.\n\nThe producer retains credit rights and the right to showcase the work in their portfolio.',
          alignment: 'left'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'music-p1', description: 'Full music production - One song', quantity: 1, unitPrice: 8000, total: 8000 },
            { id: 'music-p2', description: 'Studio vocal recording (incl. engineer)', quantity: 4, unitPrice: 500, total: 2000 },
            { id: 'music-p3', description: 'Professional mixing', quantity: 1, unitPrice: 3000, total: 3000 },
            { id: 'music-p4', description: 'Mastering', quantity: 1, unitPrice: 1500, total: 1500 },
            { id: 'music-p5', description: 'Guest musicians (guitarist + violinist)', quantity: 2, unitPrice: 2000, total: 4000 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Engagement Terms',
          items: [
            '50% deposit at signing, balance upon delivery of final master',
            'Up to 3 revision rounds included, additional rounds $300 each',
            'Cancellation up to one week before recording: 70% deposit refund',
            'Cancellation less than one week: No refund',
            'Project files stored at studio for 6 months',
            'Proposal valid for: 21 days from date of issue'
          ],
          style: 'numbered',
          theme: 'cancellation',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client/Artist Signature',
          leftLabel: 'Producer Signature',
          showDate: true
        }
      }
    ])
  },

  {
    id: 'fitness-personal-training',
    name: 'Fitness & Personal Training',
    nameHe: 'Fitness & Personal Training',
    description: 'Proposal for personal training and fitness programs',
    descriptionHe: 'Proposal for personal training and fitness programs',
    category: 'industry',
    color: 'fuchsia',
    icon: 'Dumbbell',
    coverImage: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Personal Training Program',
          subtitle: 'The best version of you starts here',
          backgroundImage: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&h=600&fit=crop',
          backgroundColor: '#c026d3',
          overlay: true,
          overlayOpacity: 0.55
        }
      },
      {
        type: 'heading',
        data: { content: 'My Philosophy', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'I believe that physical fitness is not just about appearance — it is an investment in health, energy, and self-confidence. The program I have designed for you combines strength, endurance, and functional training, while considering your current fitness level, physical limitations, and defined goals.\n\nMy approach is based on science and research, with emphasis on proper technique, injury prevention, and gradual progression.',
          alignment: 'left'
        }
      },
      {
        type: 'stats',
        data: {
          title: '',
          items: [
            { id: 'fit-s1', value: '8+', label: 'Years of Experience' },
            { id: 'fit-s2', value: '300+', label: 'Trainees Coached' },
            { id: 'fit-s3', value: '96%', label: 'Reached Their Goals' },
            { id: 'fit-s4', value: '5⭐', label: 'Average Rating' }
          ],
          columns: 4,
          color: '#c026d3'
        }
      },
      {
        type: 'heading',
        data: { content: 'Program Overview', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'What the Program Includes',
          items: [
            { icon: 'Target', text: 'Initial fitness assessment - Measurements, functional tests and goal setting' },
            { icon: 'Dumbbell', text: 'Personal training plan - 3-5 sessions per week, customized and updated' },
            { icon: 'Heart', text: 'Cardio endurance training - HIIT, running, cycling based on preference' },
            { icon: 'Ruler', text: 'Progress tracking - Body measurements and fitness tests every 4 weeks' },
            { icon: 'MessageSquare', text: 'Ongoing support - WhatsApp availability for questions and guidance' }
          ],
          columns: 1,
          iconColor: '#c026d3'
        }
      },
      {
        type: 'heading',
        data: { content: 'Training Packages', level: 2 }
      },
      {
        type: 'packages',
        data: {
          title: 'Training Plans',
          packages: [
            {
              id: 'fit-starter',
              name: 'Basic',
              price: 1200,
              currency: '$',
              period: 'per month',
              description: '8 sessions per month',
              features: [
                '2 personal sessions per week',
                'Training plan for independent days',
                'Initial fitness assessment',
                'Monthly progress tracking',
                'WhatsApp support'
              ]
            },
            {
              id: 'fit-pro',
              name: 'Advanced',
              price: 2000,
              currency: '$',
              period: 'per month',
              description: '12 sessions per month',
              features: [
                '3 personal sessions per week',
                'Training plan for independent days',
                'Basic nutrition plan',
                'Bi-weekly fitness assessment',
                'Weekly progress tracking',
                'Unlimited WhatsApp support'
              ],
              highlighted: true
            },
            {
              id: 'fit-elite',
              name: 'Elite',
              price: 3200,
              currency: '$',
              period: 'per month',
              description: '20 sessions per month',
              features: [
                '5 personal sessions per week',
                'Detailed nutrition plan + tracking',
                'Supplement consulting',
                'Weekly fitness assessment',
                'Mental training & visualization',
                'Outdoor training (running/swimming) 1x per week',
                '24/7 availability'
              ]
            }
          ]
        }
      },
      {
        type: 'heading',
        data: { content: 'Nutrition Add-on', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'For optimal results, it is recommended to combine nutritional guidance with the training program:\n\n• Personalized nutrition plan — $800 per month\n• Includes: Weekly menu, shopping list, adapted recipes, daily tracking\n• The plan is built according to goals (weight loss, muscle gain, performance)\n• Menu updates every two weeks based on progress',
          alignment: 'left'
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Terms & Rules',
          items: [
            'Minimum commitment: 3 months',
            'Payment in advance for each month, by the 1st of the month',
            'Session cancellation: Up to 12 hours ahead — can be rescheduled, less than 12 hours — session is counted',
            'Subscription freeze: Up to 2 weeks per engagement period (with medical approval)',
            'The trainer is not responsible for injuries due to non-compliance with instructions',
            'A health declaration form must be signed before starting training',
            'Proposal valid for: 14 days'
          ],
          style: 'numbered',
          theme: 'warning',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Trainee Signature',
          leftLabel: 'Trainer Signature',
          showDate: true
        }
      }
    ])
  },

  {
    id: 'social-media-management',
    name: 'Social Media Management',
    nameHe: 'Social Media',
    description: 'Proposal for social media management and content strategy',
    descriptionHe: 'Proposal for social media management and content strategy',
    category: 'industry',
    color: 'sky',
    icon: 'Share2',
    coverImage: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Social Media Management Proposal',
          subtitle: 'Content that speaks, a community that grows',
          backgroundImage: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=1200&h=600&fit=crop',
          backgroundColor: '#0284c7',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'heading',
        data: { content: 'Strategy Overview', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Professional social media management goes far beyond posting content. It is about building a brand, creating a loyal community, and turning followers into customers. After an in-depth analysis of your brand, target audience, and competitors, we have built a focused strategy that delivers results.\n\nThe strategy combines organic content, community building, and influencer collaborations — all tailored to your brand voice.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Platforms We Manage', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'Full Coverage for All Platforms',
          items: [
            { icon: 'Instagram', text: 'Instagram — Posts, Stories, Reels, IGTV and collaborations' },
            { icon: 'Facebook', text: 'Facebook — Business page management, groups, Marketplace and events' },
            { icon: 'Linkedin', text: 'LinkedIn — Professional content, articles and business networking' },
            { icon: 'Video', text: 'TikTok — Short videos, trends and viral content' },
            { icon: 'Globe', text: 'Google Business — Profile management, reviews and updates' }
          ],
          columns: 1,
          iconColor: '#0284c7'
        }
      },
      {
        type: 'heading',
        data: { content: 'Monthly Content Calendar', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Each month you will receive a detailed content calendar including:\n\n• 12 feed posts (3 per week) — Photos, infographics, carousels\n• 20 stories per month (5 per week) — Polls, behind the scenes, Q&A\n• 4 Reels/TikToks per month — Short videos with professional editing\n• Copywriting for all posts — Including tailored hashtags\n• Community management — Responding to comments and messages within 4 business hours',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Success Metrics (KPIs)', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            '15%-20% follower growth per month',
            'Engagement Rate above 4%',
            '30% increase in impressions within 3 months',
            'Website traffic referral - 25% increase in visits from social',
            'Average response time to messages - Under 4 hours'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Management Packages', level: 2 }
      },
      {
        type: 'packages',
        data: {
          title: 'Social Management Plans',
          packages: [
            {
              id: 'sm-starter',
              name: 'Starter',
              price: 3500,
              currency: '$',
              period: 'per month',
              description: 'One platform',
              features: [
                'Management of one platform of your choice',
                '12 posts per month',
                '12 stories per month',
                'Copywriting + graphic design',
                'Monthly performance report',
                'Monthly summary meeting'
              ]
            },
            {
              id: 'sm-growth',
              name: 'Growth',
              price: 6000,
              currency: '$',
              period: 'per month',
              description: '2 platforms',
              features: [
                'Management of 2 platforms',
                '12 posts + 20 stories per month',
                '4 Reels/short videos',
                'Daily community management',
                'Hashtag strategy',
                'Bi-weekly performance report',
                'Monthly strategy meeting'
              ],
              highlighted: true
            },
            {
              id: 'sm-premium',
              name: 'Premium',
              price: 10000,
              currency: '$',
              period: 'per month',
              description: 'All platforms',
              features: [
                'Management of all platforms (up to 4)',
                '20 posts + 30 stories per month',
                '8 Reels/short videos',
                'Monthly content photoshoot (1 shoot day)',
                'Paid campaign management',
                'Influencer collaborations',
                'Weekly report + bi-weekly meeting',
                'Comment responses 7 days a week'
              ]
            }
          ]
        }
      },
      {
        type: 'heading',
        data: { content: 'Reporting & Transparency', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Each month you will receive a detailed performance report including: growth analysis, engagement metrics, top content performance, audience analysis, improvement recommendations, and a plan for the following month. The report includes comparisons to previous months and trend analysis.',
          alignment: 'left'
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Engagement Terms',
          items: [
            'Minimum commitment: 3 months (6 recommended for optimal results)',
            'Monthly payment by the 5th of each month',
            'Client provides full access to all platforms',
            'Weekly content approval — Client approves within 48 hours',
            'Paid advertising budget not included (managed separately)',
            'Cancellation with 30 days advance notice',
            'All created content belongs to the client',
            'Proposal valid for: 14 days'
          ],
          style: 'numbered',
          theme: 'general',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Social Media Manager Signature',
          showDate: true
        }
      }
    ])
  },

  {
    id: 'translation-localization',
    name: 'Translation & Localization',
    nameHe: 'Translation & Localization',
    description: 'Proposal for translation, localization and interpretation services',
    descriptionHe: 'Proposal for translation, localization and interpretation services',
    category: 'industry',
    color: 'amber',
    icon: 'Languages',
    coverImage: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Translation & Localization Services Proposal',
          subtitle: 'Bridging languages and cultures',
          backgroundImage: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=1200&h=600&fit=crop',
          backgroundColor: '#d97706',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'heading',
        data: { content: 'Languages & Areas of Expertise', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Our firm specializes in professional translation and localization with a team of certified translators and native speakers. We offer translation in 12 languages with expertise in law, medicine, technology, marketing, and academia.\n\nEvery translation undergoes a rigorous QA process including Translation, Editing, and Proofreading (TEP) by at least two independent translators.',
          alignment: 'left'
        }
      },
      {
        type: 'benefits',
        data: {
          title: 'Main Languages',
          items: [
            { icon: 'Globe', text: 'English ⟷ Spanish — General, legal, technical, marketing and academic translation' },
            { icon: 'Globe', text: 'English ⟷ Arabic — Official, notarized and business translation' },
            { icon: 'Globe', text: 'English ⟷ Russian — Document, medical and legal translation' },
            { icon: 'Globe', text: 'English ⟷ French/German/Portuguese — General and business translation' },
            { icon: 'Globe', text: 'English ⟷ European and Asian languages — Localization and technical translation' }
          ],
          columns: 1,
          iconColor: '#d97706'
        }
      },
      {
        type: 'heading',
        data: { content: 'Service Types', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Written translation — Documents, contracts, websites, marketing materials, technical manuals',
            'Notarized translation — Certificates, official documents, apostille (recognized by authorities)',
            'Localization — Adapting software, apps and websites for the local market',
            'Simultaneous interpretation — Conferences, board meetings, international events',
            'Consecutive interpretation — Business meetings, negotiations, professional tours',
            'Transcription & translation — From audio/video files to translated text'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Delivery Times', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'We commit to precise delivery times:\n\n• Standard document (up to 5 pages): 2-3 business days\n• Medium document (5-20 pages): 5-7 business days\n• Large project (20+ pages): Per individual assessment\n• Express translation: 50% surcharge, delivery within 24 hours\n• Notarized translation: 3-5 business days (including verification)\n\nDelivery times are calculated from receipt of final material and order confirmation.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Pricing', level: 2 }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'trans-p1', description: 'General translation (English-Spanish) — per page', quantity: 20, unitPrice: 120, total: 2400 },
            { id: 'trans-p2', description: 'Professional/legal translation — per page', quantity: 10, unitPrice: 180, total: 1800 },
            { id: 'trans-p3', description: 'Notarized translation + stamp — per document', quantity: 3, unitPrice: 250, total: 750 },
            { id: 'trans-p4', description: 'Localization (per word)', quantity: 5000, unitPrice: 0.55, total: 2750 },
            { id: 'trans-p5', description: 'Editing & proofreading — per page', quantity: 30, unitPrice: 60, total: 1800 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Confidentiality & Terms',
          items: [
            'All materials are handled with full confidentiality — NDA available for signing upon request',
            'Translation page = 250 words in source language',
            'Payment: 50% deposit, 50% upon delivery of translation',
            'One revision round included, additional revisions at hourly rate ($200/hour)',
            'Significant change requests after draft approval — at additional cost',
            'Order cancellation: Full refund if work has not started, 50% if started',
            'Proposal valid for: 30 days'
          ],
          style: 'numbered',
          theme: 'privacy',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Translation Office Signature',
          showDate: true
        }
      }
    ])
  },

  {
    id: 'cleaning-maintenance',
    name: 'Cleaning & Maintenance',
    nameHe: 'Cleaning & Maintenance',
    description: 'Proposal for cleaning and maintenance services',
    descriptionHe: 'Proposal for cleaning and maintenance services',
    category: 'industry',
    color: 'stone',
    icon: 'Sparkles',
    coverImage: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Cleaning & Maintenance Services Proposal',
          subtitle: 'A clean, professional and welcoming environment',
          backgroundImage: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1200&h=600&fit=crop',
          backgroundColor: '#78716c',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'heading',
        data: { content: 'Service Areas', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Our company provides professional cleaning services for businesses, offices, clinics, educational institutions, and private homes. Our staff undergo professional training, background checks, and are equipped with advanced equipment and eco-friendly cleaning products.\n\nWe are committed to uncompromising service quality — every job is inspected by a dedicated quality supervisor.',
          alignment: 'left'
        }
      },
      {
        type: 'stats',
        data: {
          title: '',
          items: [
            { id: 'clean-s1', value: '12+', label: 'Years of Operation' },
            { id: 'clean-s2', value: '350+', label: 'Regular Clients' },
            { id: 'clean-s3', value: '50+', label: 'Professional Staff' },
            { id: 'clean-s4', value: '100%', label: 'Eco-Friendly' }
          ],
          columns: 4,
          color: '#78716c'
        }
      },
      {
        type: 'heading',
        data: { content: 'Frequency & Service Plans', level: 2 }
      },
      {
        type: 'packages',
        data: {
          title: 'Cleaning Plans',
          packages: [
            {
              id: 'clean-weekly',
              name: 'Weekly',
              price: 1200,
              currency: '$',
              period: 'per month',
              description: 'Cleaning once a week',
              features: [
                'General weekly cleaning',
                'Vacuuming & floor mopping',
                'Restroom & kitchenette cleaning',
                'Dusting surfaces',
                'Trash can emptying',
                'Suitable for offices up to 1,000 sq ft'
              ]
            },
            {
              id: 'clean-biweekly',
              name: 'Twice Weekly',
              price: 2200,
              currency: '$',
              period: 'per month',
              description: 'Cleaning twice a week',
              features: [
                'All weekly plan services',
                'Cleaning twice a week',
                'Interior window cleaning (monthly)',
                'Work surface disinfection',
                'Soap & paper dispenser refills',
                'Weekly cleaning report',
                'Suitable for offices up to 2,000 sq ft'
              ],
              highlighted: true
            },
            {
              id: 'clean-daily',
              name: 'Daily',
              price: 4500,
              currency: '$',
              period: 'per month',
              description: 'Daily cleaning (Mon-Fri)',
              features: [
                'Comprehensive daily cleaning',
                'Daily disinfection of all surfaces',
                'Window cleaning (bi-weekly)',
                'Floor polishing (monthly)',
                'Cleaning supply inventory management',
                'Weekly quality inspector',
                'Flexible working hours',
                'Suitable for any office size'
              ]
            }
          ]
        }
      },
      {
        type: 'heading',
        data: { content: 'Cleaning Checklist', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Vacuuming & mopping all floors (tile, hardwood, carpets)',
            'Wiping & disinfecting all work surfaces, tables and door handles',
            'Deep cleaning of restrooms — toilets, sinks, mirrors, floor',
            'Kitchen/kitchenette cleaning — sink, surfaces, appliances (exterior)',
            'Emptying all trash cans and replacing bags',
            'Dusting all surfaces, shelves and decorative elements',
            'Cleaning phones, keyboards and screens (by arrangement)',
            'Checking soap, toilet paper and hand towel supplies'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Cleaning Products', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'We use only health authority-approved cleaning products:\n\n• Eco-friendly products with green certification\n• Disinfectants meeting EN 14476 standard (anti-viral)\n• Products without strong odor — suitable for work environments\n• Hypoallergenic products — safe for environments with children and sensitive individuals',
          alignment: 'left'
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Terms & Commitments',
          items: [
            'Minimum commitment: 6 months',
            'Monthly payment on the 10th via standing order',
            'Third-party liability and professional responsibility insurance — included',
            'Liability for breakage/damage caused by our staff — full coverage',
            'Frequency change: With 14 days advance notice',
            'Cancellation: 30 days notice',
            'Satisfaction guarantee — Re-clean at no cost within 24 hours',
            'Proposal valid for: 21 days'
          ],
          style: 'numbered',
          theme: 'warranty',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Cleaning Company Signature',
          showDate: true
        }
      }
    ])
  },

  {
    id: 'landscaping-gardening',
    name: 'Landscaping & Gardening',
    nameHe: 'Landscaping & Gardening',
    description: 'Proposal for landscaping, garden design and maintenance',
    descriptionHe: 'Proposal for garden design, landscaping and ongoing maintenance',
    category: 'industry',
    color: 'zinc',
    icon: 'TreePine',
    coverImage: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Garden & Landscape Design Proposal',
          subtitle: 'Green spaces that breathe life',
          backgroundImage: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=1200&h=600&fit=crop',
          backgroundColor: '#52525b',
          overlay: true,
          overlayOpacity: 0.5
        }
      },
      {
        type: 'heading',
        data: { content: 'Design Concept', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Following the site tour and introductory meeting, we have designed a garden that combines natural beauty with functionality. The design considers local climate conditions, wind and sun direction, your budget, and your lifestyle.\n\nOur approach favors climate-adapted vegetation (Xeriscape) that requires minimal irrigation and maintenance, while maintaining a lush green appearance year-round.',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Plant Selection', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'Garden Components',
          items: [
            { icon: 'TreePine', text: 'Ornamental & shade trees — Olive, Brachychiton, Pepper tree, Acacia — Heat-resistant varieties' },
            { icon: 'Flower2', text: 'Flowering shrubs — Lavender, Rosemary, Bougainvillea, Gazania — Year-round blooming' },
            { icon: 'Leaf', text: 'Ground cover — Native grass, Lippia, Frankenia — Water-saving and low maintenance' },
            { icon: 'Sprout', text: 'Herb garden — Sage, Oregano, Mint, Basil — Functional and aesthetic combination' },
            { icon: 'Droplets', text: 'Irrigation system — Smart drip with weather-based irrigation controller' }
          ],
          columns: 1,
          iconColor: '#52525b'
        }
      },
      {
        type: 'heading',
        data: { content: 'Hardscape Elements', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Paths & walkways — Natural stone/polished concrete in modern finish',
            'Pergola — Whitened pine wood 4x3 meters with shade',
            'Wood/composite deck — Raised seating area 130 sq ft',
            'Garden lighting — Low-voltage LED, plant spotlights and path lighting',
            'Drainage system — Surface and subsurface drainage to prevent flooding',
            'Fence/partition — Wood/metal fence with climbing vegetation'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Maintenance Plan', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'To keep your garden at its best, we offer an ongoing maintenance plan:\n\n• Weekly visit: Pruning, weeding, leaf collection, irrigation check\n• Monthly: Fertilizing, preventive spraying, irrigation system check\n• Seasonal: Tree pruning, seasonal flower replacement, winter/summer prep\n• Annual: Lawn renewal, mulch refresh, mature tree care\n\nOngoing maintenance cost: $800-$1,500 per month (depending on garden size)',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Timeline & Execution Phases', level: 2 }
      },
      {
        type: 'timeline',
        data: {
          title: 'Project Phases',
          items: [
            { id: 'garden-t1', title: 'Planning & Design', description: 'Master plan, plant selection, renderings and technical specs', date: 'Weeks 1-2' },
            { id: 'garden-t2', title: 'Infrastructure Prep', description: 'Clearing, ground leveling, drainage and irrigation system', date: 'Weeks 3-4' },
            { id: 'garden-t3', title: 'Hardscape Work', description: 'Paths, deck, pergola, lighting and fences', date: 'Weeks 5-7' },
            { id: 'garden-t4', title: 'Planting & Finishing', description: 'Planting trees, shrubs, lawn, mulch and styling', date: 'Weeks 8-9' }
          ],
          layout: 'vertical',
          color: '#52525b'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'garden-p1', description: 'Garden planning & design incl. renderings', quantity: 1, unitPrice: 5000, total: 5000 },
            { id: 'garden-p2', description: 'Infrastructure prep & irrigation system', quantity: 1, unitPrice: 8000, total: 8000 },
            { id: 'garden-p3', description: 'Hardscape work (paths, deck, pergola)', quantity: 1, unitPrice: 22000, total: 22000 },
            { id: 'garden-p4', description: 'Plants & planting', quantity: 1, unitPrice: 7000, total: 7000 },
            { id: 'garden-p5', description: 'LED garden lighting', quantity: 1, unitPrice: 4500, total: 4500 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Terms & Commitments',
          items: [
            'Payment in 3 stages: 40% at signing, 40% mid-execution, 20% at handover',
            'Plant warranty: One year — Free replacement for plants that did not take root',
            'Hardscape warranty: 3 years',
            'Irrigation system warranty: 2 years',
            'Changes to the plan after start of execution — At additional cost accordingly',
            'Garden maintenance is the client\'s responsibility (or under a separate maintenance contract)',
            'Proposal valid for: 30 days'
          ],
          style: 'numbered',
          theme: 'warranty',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client Signature',
          leftLabel: 'Gardener/Landscape Designer Signature',
          showDate: true
        }
      }
    ])
  },

  {
    id: 'import-logistics',
    name: 'Import & Logistics',
    nameHe: 'Import & Logistics',
    description: 'Proposal for import, customs and logistics services',
    descriptionHe: 'Proposal for import, customs and logistics services',
    category: 'industry',
    color: 'violet',
    icon: 'Ship',
    coverImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&h=600&fit=crop',
    blocks: createTemplateBlocks([
      {
        type: 'cover',
        data: {
          title: 'Import & Logistics Services Proposal',
          subtitle: 'From factory to your door — with complete confidence',
          backgroundImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&h=600&fit=crop',
          backgroundColor: '#e14d9a',
          overlay: true,
          overlayOpacity: 0.6
        }
      },
      {
        type: 'heading',
        data: { content: 'Services Overview', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'Our company specializes in goods import and international shipping with over 15 years of experience. We handle the entire supply chain — from coordinating with the overseas supplier, through sea/air shipping, customs handling and cargo release, to final delivery to the client.\n\nOur team includes licensed customs brokers, international trade experts and experienced logistics managers who ensure your goods arrive on time, intact and at the best cost.',
          alignment: 'left'
        }
      },
      {
        type: 'stats',
        data: {
          title: '',
          items: [
            { id: 'imp-s1', value: '15+', label: 'Years of Experience' },
            { id: 'imp-s2', value: '2,000+', label: 'Shipments per Year' },
            { id: 'imp-s3', value: '40+', label: 'Destination Countries' },
            { id: 'imp-s4', value: '99.5%', label: 'On-Time Delivery' }
          ],
          columns: 4,
          color: '#e14d9a'
        }
      },
      {
        type: 'heading',
        data: { content: 'Shipping Routes', level: 2 }
      },
      {
        type: 'benefits',
        data: {
          title: 'Shipping Options',
          items: [
            { icon: 'Ship', text: 'Sea freight FCL — Full container (20\'/40\') from any port worldwide, 14-35 transit days' },
            { icon: 'Ship', text: 'Sea freight LCL — Partial cargo, container consolidation, cost-effective for small quantities' },
            { icon: 'Plane', text: 'Air freight — Express (3-5 days) or standard (7-10 days), for urgent cargo' },
            { icon: 'Truck', text: 'Land freight — Trucks from Europe and Turkey, 7-14 days' },
            { icon: 'Package', text: 'Door to Door shipping — Including pickup from supplier and delivery to client' }
          ],
          columns: 1,
          iconColor: '#e14d9a'
        }
      },
      {
        type: 'heading',
        data: { content: 'Customs Handling', level: 2 }
      },
      {
        type: 'list',
        data: {
          items: [
            'Customs classification & pre-clearance tax rate assessment',
            'Import declaration preparation & full customs documentation',
            'License & permit handling (standards institute, health dept., tax authority)',
            'Cargo release from port/airport',
            'Handling customs exemptions & discounts (free trade agreements)',
            'Consulting on import regulations, CE marking & local standards'
          ],
          style: 'check'
        }
      },
      {
        type: 'heading',
        data: { content: 'Warehousing & Distribution', level: 2 }
      },
      {
        type: 'text',
        data: {
          content: 'We operate an approved 55,000 sq ft warehouse near port area:\n\n• Dry, climate-controlled and refrigerated storage — Based on cargo type\n• Computerized inventory management — Real-time online tracking\n• Picking & packing services — Order preparation and shipments\n• Nationwide distribution — Partnership with leading courier companies\n• Warehouse insurance — Full coverage for stored goods',
          alignment: 'left'
        }
      },
      {
        type: 'heading',
        data: { content: 'Estimated Timeline', level: 2 }
      },
      {
        type: 'timeline',
        data: {
          title: 'Import Process',
          items: [
            { id: 'imp-t1', title: 'Order & Coordination', description: 'Supplier coordination, proforma approval, L/C opening', date: 'Week 1' },
            { id: 'imp-t2', title: 'Manufacturing & Collection', description: 'Production tracking, quality control, factory pickup', date: 'Weeks 2-4' },
            { id: 'imp-t3', title: 'Shipping & Transit', description: 'Sea/air freight, real-time GPS tracking', date: 'Weeks 5-8' },
            { id: 'imp-t4', title: 'Customs & Release', description: 'Customs brokerage, inspections, release and transport to warehouse/client', date: 'Week 9' }
          ],
          layout: 'horizontal',
          color: '#e14d9a'
        }
      },
      {
        type: 'price_table',
        data: {
          items: [
            { id: 'imp-p1', description: 'Customs brokerage & cargo release', quantity: 1, unitPrice: 2500, total: 2500 },
            { id: 'imp-p2', description: 'Sea freight — 40\' container from China', quantity: 1, unitPrice: 12000, total: 12000 },
            { id: 'imp-p3', description: 'Cargo insurance (1% of goods value)', quantity: 1, unitPrice: 3000, total: 3000 },
            { id: 'imp-p4', description: 'Storage — First month', quantity: 1, unitPrice: 2000, total: 2000 },
            { id: 'imp-p5', description: 'Domestic transport to client warehouse', quantity: 1, unitPrice: 1500, total: 1500 },
            { id: 'imp-p6', description: 'Supplier coordination & sourcing', quantity: 1, unitPrice: 3500, total: 3500 }
          ],
          showQuantity: true,
          showUnitPrice: true
        }
      },
      {
        type: 'terms',
        data: {
          title: 'Insurance & Engagement Terms',
          items: [
            'Cargo insurance: All Risks coverage from factory departure to destination',
            'Transit damage liability covered by cargo insurance',
            'Delays due to Force Majeure are not our responsibility',
            'Payment: 50% deposit on order, 50% upon cargo release',
            'Shipping prices may change based on market fluctuations (BAF, CAF)',
            'Client is responsible for import taxes, customs duties and VAT to authorities',
            'Order cancellation: Before shipping — handling cost only, after shipping — no cancellation',
            'Proposal valid for: 14 days (shipping prices valid for 7 days)'
          ],
          style: 'numbered',
          theme: 'delivery',
          variant: 'filled',
          showIcon: true
        }
      },
      {
        type: 'dual_signature',
        data: {
          rightLabel: 'Client/Importer Signature',
          leftLabel: 'Logistics Company Signature',
          showDate: true
        }
      }
    ])
  },

  // ===== END OF INDUSTRY TEMPLATES =====

  {
    id: 'empty',
    name: 'Blank Template',
    nameHe: 'Blank Template',
    description: 'Start from scratch',
    descriptionHe: 'Start from scratch',
    category: 'basic',
    color: 'slate',
    icon: 'Plus',
    coverImage: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=1200&h=600&fit=crop',
    blocks: []
  }
]

export function getTemplateById(id: string): ProposalTemplate | undefined {
  return proposalTemplates.find(t => t.id === id)
}

export function getTemplatesByCategory(category: ProposalTemplate['category']): ProposalTemplate[] {
  return proposalTemplates.filter(t => t.category === category)
}
